/**
 * ControlChartDashboard - Change Management Control Chart (Change Log)
 * 
 * Full-featured dashboard for managing control chart entries:
 * - Change orders, extras, RFIs, POCRs, POCNs
 * - Cost tracking (submitted vs approved prices)
 * - Invoice tracking
 * - Lump Sum vs T&M billing
 * - PDF generation for change orders (Lump Sum + T&M templates)
 */

import React, { useState, useEffect, useMemo, useCallback, memo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { usePresence, ActiveViewers } from '../common/ViewingTemplate';
import useFilterTemplate from '../../hooks/useFilterTemplate';
import FilterTemplateDropdown from '../common/FilteringTemplate/FilterTemplateDropdown';
import ChangeTypeViewDropdown from './ChangeTypeViewDropdown';
import { ListenButton } from '../common/AudioGuide';
import HelpWithVideoTour from '../Help/HelpWithVideoTour';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, Line, AreaChart, Area, ComposedChart
} from 'recharts';
import { DISCIPLINES as CODING_DISCIPLINES } from '../../data/codingStructureData';
import useColumnOrder from '../../hooks/useColumnOrder';
import useFrozenColumns from '../../hooks/useFrozenColumns';
import ColumnConfigPanel from '../common/ColumnConfigPanel';
import { applyColumnOrder } from '../../utils/columnLayout';
import { drawLetterhead, loadLetterhead } from '../../utils/pdfLetterhead';
import { exportFilename } from '../../utils/nomenclature';

const DISCIPLINES = CODING_DISCIPLINES.map(d => d.name);

const COLUMNS = [
  { key: 'job_number', label: 'JOB#', width: 100, required: true },
  { key: 'discipline', label: 'Discipline', width: 120 },
  { key: 'new_extra_number', label: 'Extra', width: 120 },
  { key: 'status', label: 'Status', width: 100 },
  { key: 'billing_type', label: 'Change Type', width: 130 },
  { key: 'description', label: 'Description', width: 250 },
  { key: 'commencement_date', label: 'Commencement Date', width: 150, type: 'date' },
  { key: 'rfi_number', label: 'RFI No.', width: 100 },
  { key: 'pocr_number', label: 'POCR #', width: 100 },
  { key: 'pocn_number', label: 'POCN#', width: 100 },
  { key: 'estimated_hours', label: 'Estimated Hours', width: 130, type: 'number' },
  { key: 'estimated_price', label: 'Estimated $', width: 130, type: 'currency' },
  { key: 'submitted_hours', label: 'Submitted Hours', width: 130, type: 'number' },
  { key: 'submitted_price', label: 'Submitted $', width: 140, type: 'currency' },
  { key: 'approved_price', label: 'Approved Price', width: 140, type: 'currency' },
  { key: 'maestro_status', label: 'Maestro', width: 110 },
  { key: 'labour_total', label: 'Direct Total', width: 130, type: 'currency' },
  { key: 'material_total', label: 'Material Total', width: 130, type: 'currency' },
  { key: 'subcontractor_total', label: 'Subcontractor Total', width: 140, type: 'currency' },
  { key: 'equipment_total', label: 'Equipment Total', width: 130, type: 'currency' },
  { key: 'room_and_board', label: 'Room & Board', width: 130, type: 'currency' },
  { key: 'transport', label: 'Transport', width: 120, type: 'currency' },
  { key: 'self_assessed_tax', label: 'Tax', width: 100, type: 'currency' },
  { key: 'change_order_total', label: 'CO Total', width: 130, type: 'currency' },
  { key: 'schedule_total_days', label: 'Schedule (days)', width: 120, type: 'number' },
  { key: 'date_completed', label: 'Date Completed', width: 140, type: 'date' },
  { key: 'invoice_number', label: 'Invoice #', width: 110 },
  { key: 'invoice_date', label: 'Invoice Date', width: 120, type: 'date' },
  { key: 'notes', label: 'Notes', width: 200 },
  { key: 'opened_by', label: 'Opened By', width: 180 },
  { key: 'ctc_wbs', label: 'CTC/WBS', width: 120 }
];

const NUMERIC_COLUMN_KEYS = COLUMNS
  .filter(c => c.type === 'number' || c.type === 'currency')
  .map(c => c.key);

const CHECKBOX_COL_WIDTH = 50;
const MAX_FROZEN_COLUMNS = 6;
const DEFAULT_FROZEN_KEYS = ['new_extra_number', 'description'];

const DEFAULT_CLARIFICATION = 'The impacts to the schedule and associated cost from schedule delays as a result of this change have not been assessed and will be communicated through a further change request at a later date. These impacts noted above do not necessarily correspond to a 1 for 1 push in the critical path of the schedule and will be clearly represented in the schedule updates.';

const BILLING_TYPE_OPTIONS = ['Lump Sum', 'T&M', 'Tracking Only', 'Material Supply Only'];

// Per-project Change Log configuration defaults (used when a project has no saved config).
// The scalar fields are the project-wide DEFAULTS that seed new trades and act as the
// fallback for custom trades. `trades` is the list of configured trades, each carrying its
// own calendar, ST/OT/DT rates, and Room & Board / Transport per-diems.
const DEFAULT_CONFIG = {
  straight_hours_per_week: 40,
  overtime_hours_per_week: 20,
  double_time_hours_per_week: 0,
  days_per_week: 7,
  room_board_per_diem: 0,
  transport_per_diem: 0,
  straight_rate: 0,
  overtime_rate: 0,
  double_time_rate: 0,
  trades: [],
};

// Build a fresh trade-config object from a base config's default values.
const makeTradeFromDefaults = (cfg, name = '') => {
  const c = cfg || DEFAULT_CONFIG;
  return {
    name,
    straight_hours_per_week: parseFloat(c.straight_hours_per_week) || 0,
    overtime_hours_per_week: parseFloat(c.overtime_hours_per_week) || 0,
    double_time_hours_per_week: parseFloat(c.double_time_hours_per_week) || 0,
    days_per_week: parseFloat(c.days_per_week) || 0,
    straight_rate: parseFloat(c.straight_rate) || 0,
    overtime_rate: parseFloat(c.overtime_rate) || 0,
    double_time_rate: parseFloat(c.double_time_rate) || 0,
    room_board_per_diem: parseFloat(c.room_board_per_diem) || 0,
    transport_per_diem: parseFloat(c.transport_per_diem) || 0,
  };
};

// Resolve the effective calendar/per-diem config for a trade row in a change order.
// Order: the trade's own snapshot (cfg) -> a matching configured trade by name -> project default.
const resolveTradeConfig = (trade, config) => {
  const cfg = config || DEFAULT_CONFIG;
  if (trade && trade.cfg) return trade.cfg;
  const name = ((trade && trade.trade_name) || '').trim().toLowerCase();
  const match = Array.isArray(cfg.trades)
    ? cfg.trades.find(t => (t.name || '').trim().toLowerCase() === name && name !== '')
    : null;
  return match || cfg;
};

// Derive hourly Room & Board / Transport costs from a (trade or project) work-calendar config.
// per_hour = per_diem * days_per_week / hours_per_week   (hours_per_week = ST + OT + DT per week)
const computePerHourCosts = (cfg) => {
  const c = cfg || DEFAULT_CONFIG;
  const hoursPerWeek =
    (parseFloat(c.straight_hours_per_week) || 0) +
    (parseFloat(c.overtime_hours_per_week) || 0) +
    (parseFloat(c.double_time_hours_per_week) || 0);
  const daysPerWeek = parseFloat(c.days_per_week) || 0;
  const rbPerHour = hoursPerWeek > 0 ? ((parseFloat(c.room_board_per_diem) || 0) * daysPerWeek) / hoursPerWeek : 0;
  const transportPerHour = hoursPerWeek > 0 ? ((parseFloat(c.transport_per_diem) || 0) * daysPerWeek) / hoursPerWeek : 0;
  return { hoursPerWeek, daysPerWeek, rbPerHour, transportPerHour };
};

// Distribute a single total-hours figure across Straight/Overtime/Double Time using a trade's
// weekly calendar: fill ST up to the weekly ST cap, then OT, then DT, repeating week over week
// until the total is consumed. (No crew scaling -- proration is by calendar only.)
const distributeHoursByCalendar = (totalHours, cfg) => {
  const c = cfg || DEFAULT_CONFIG;
  const stCap = parseFloat(c.straight_hours_per_week) || 0;
  const otCap = parseFloat(c.overtime_hours_per_week) || 0;
  const dtCap = parseFloat(c.double_time_hours_per_week) || 0;
  let remaining = parseFloat(totalHours) || 0;
  if (remaining <= 0) return { st: 0, ot: 0, dt: 0 };

  // If there is no weekly capacity configured, put everything in Straight Time.
  if (stCap + otCap + dtCap <= 0) return { st: remaining, ot: 0, dt: 0 };

  let st = 0, ot = 0, dt = 0;
  // Guard against infinite loops; weeks needed is bounded by remaining / smallest positive cap.
  let safety = 100000;
  while (remaining > 1e-9 && safety-- > 0) {
    const takeSt = Math.min(remaining, stCap); st += takeSt; remaining -= takeSt;
    if (remaining <= 1e-9) break;
    const takeOt = Math.min(remaining, otCap); ot += takeOt; remaining -= takeOt;
    if (remaining <= 1e-9) break;
    const takeDt = Math.min(remaining, dtCap); dt += takeDt; remaining -= takeDt;
    // If a full week added nothing (all caps zero except already-filled), dump the rest into ST.
    if (takeSt + takeOt + takeDt <= 1e-9) { st += remaining; remaining = 0; }
  }
  const round = (n) => Math.round(n * 100) / 100;
  return { st: round(st), ot: round(ot), dt: round(dt) };
};

const CHANGE_TYPE_COLORS = {
  'Lump Sum':             { bg: '#f0f9ff', border: '#bae6fd', text: '#0369a1' },
  'T&M':                  { bg: '#fef9c3', border: '#fde68a', text: '#92400e' },
  'Tracking Only':        { bg: '#f3e8ff', border: '#d8b4fe', text: '#7e22ce' },
  'Material Supply Only': { bg: '#ecfdf5', border: '#a7f3d0', text: '#065f46' },
};

// ============================================
// STATUS MODEL (lifecycle of a change entry)
// ============================================
// Pending → Submitted → Approved, plus Tracking (from change type),
// and the manual terminal states Cancelled & Rejected.
const STATUS_OPTIONS = ['pending', 'submitted', 'approved', 'tracking', 'cancelled', 'rejected'];

const STATUS_META = {
  pending:   { label: 'Pending',   row: '#ffffff', rowText: '#374151', badgeBg: '#f1f5f9', badgeText: '#475569', dot: '#94a3b8', strike: false },
  submitted: { label: 'Submitted', row: '#dcfce7', rowText: '#166534', badgeBg: '#bbf7d0', badgeText: '#166534', dot: '#4ade80', strike: false },
  approved:  { label: 'Approved',  row: '#86efac', rowText: '#14532d', badgeBg: '#22c55e', badgeText: '#ffffff', dot: '#16a34a', strike: false },
  tracking:  { label: 'Tracking',  row: '#fef08a', rowText: '#854d0e', badgeBg: '#fde047', badgeText: '#854d0e', dot: '#eab308', strike: false },
  cancelled: { label: 'Cancelled', row: '#f3f4f6', rowText: '#dc2626', badgeBg: '#e5e7eb', badgeText: '#dc2626', dot: '#9ca3af', strike: true  },
  rejected:  { label: 'Rejected',  row: '#9ca3af', rowText: '#b91c1c', badgeBg: '#6b7280', badgeText: '#fee2e2', dot: '#4b5563', strike: true  },
};

// Derive a lifecycle status from the entry's cost fields / change type.
const deriveStatus = (row = {}) => {
  if (row.billing_type === 'Tracking Only') return 'tracking';
  if (parseFloat(row.approved_price) > 0) return 'approved';
  if (parseFloat(row.submitted_price) > 0) return 'submitted';
  if (parseFloat(row.estimated_price) > 0) return 'pending';
  return 'pending';
};

// Resolve the effective status: honour explicit statuses, otherwise derive
// (covers legacy 'active'/null rows created before the lifecycle existed).
const getEntryStatus = (row = {}) => {
  const s = (row.status || '').toLowerCase();
  if (STATUS_META[s]) return s;
  return deriveStatus(row);
};

// Simple scalars that can be edited in the grid. Cost-builder totals, status,
// change type, and extra# stay in the full form (double-click the row).
const INLINE_EDITABLE_KEYS = new Set([
  'description', 'notes',
  'invoice_number', 'invoice_date', 'date_completed', 'commencement_date',
  'rfi_number', 'pocr_number', 'pocn_number', 'ctc_wbs',
  'estimated_hours', 'estimated_price', 'approved_price',
]);

const toDateInputValue = (raw) => {
  if (raw === null || raw === undefined || raw === '') return '';
  const s = String(raw);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const initialInlineEditValue = (raw, col) => {
  if (col.type === 'date') return toDateInputValue(raw);
  if (col.type === 'number' || col.type === 'currency') {
    if (raw === null || raw === undefined || raw === '') return '';
    const n = Number(raw);
    if (!Number.isFinite(n) || n === 0) return '';
    return String(n);
  }
  return raw == null ? '' : String(raw);
};

const coerceInlineValue = (col, raw) => {
  if (raw === '' || raw === null || raw === undefined) {
    if (col.type === 'number' || col.type === 'currency' || col.type === 'date') return null;
    return '';
  }
  if (col.type === 'number' || col.type === 'currency') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  if (col.type === 'date') return toDateInputValue(raw) || null;
  return raw;
};

const inlineValuesEqual = (col, current, next) => {
  if (col.type === 'number' || col.type === 'currency') {
    return (Number(current) || 0) === (Number(next) || 0);
  }
  if (col.type === 'date') return toDateInputValue(current) === toDateInputValue(next);
  return String(current ?? '') === String(next ?? '');
};

// Discipline view palette (for the "View by discipline" dropdown dots/charts)
const DISCIPLINE_PALETTE = ['#0ea5e9', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444', '#ec4899', '#14b8a6', '#6366f1', '#84cc16', '#f97316'];

const measureTextWidth = (() => {
  let canvas;
  return (text, font = '14px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif') => {
    if (!canvas) canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.font = font;
    return ctx.measureText(String(text ?? '')).width;
  };
})();

// ============================================
// REUSABLE COMPONENTS
// ============================================

const FilterIcon = memo(({ isActive, color = '#666' }) => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill={isActive ? '#007bff' : color}>
    <path d="M1.5 1.5A.5.5 0 0 1 2 1h12a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.128.334L10 8.692V13.5a.5.5 0 0 1-.342.474l-3 1A.5.5 0 0 1 6 14.5V8.692L1.628 3.834A.5.5 0 0 1 1.5 3.5v-2z"/>
  </svg>
));

const InlineCellEditor = memo(({ col, value, onChange, onSave, onCancel }) => (
  <input
    type={col.type === 'date' ? 'date' : (col.type === 'number' || col.type === 'currency') ? 'number' : 'text'}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    onFocus={(e) => e.target.select()}
    onBlur={(e) => onSave(e.target.value)}
    onKeyDown={(e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.target.blur();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    }}
    onClick={(e) => e.stopPropagation()}
    onDoubleClick={(e) => e.stopPropagation()}
    autoFocus
    step={col.type === 'number' || col.type === 'currency' ? 'any' : undefined}
    style={{
      width: '100%',
      padding: '4px 6px',
      border: '2px solid #dc2626',
      borderRadius: '4px',
      fontSize: '0.85rem',
      boxSizing: 'border-box',
    }}
  />
));

const KPICard = memo(({ title, value, subtitle, icon, color }) => (
  <div style={{
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '1.25rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    borderLeft: `4px solid ${color}`,
    flex: '1 1 180px',
    minWidth: '160px'
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '500' }}>{title}</span>
      <span style={{ fontSize: '1.5rem' }}>{icon}</span>
    </div>
    <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#1e293b', margin: '0.5rem 0' }}>
      {value}
    </div>
    {subtitle && (
      <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{subtitle}</div>
    )}
  </div>
));

const Button = memo(({ children, onClick, variant = 'default', icon, disabled, type = 'button' }) => {
  const variants = {
    default: { bg: 'white', color: '#374151', border: '1px solid #d1d5db' },
    primary: { bg: '#dc3545', color: 'white', border: 'none' },
    success: { bg: '#28a745', color: 'white', border: 'none' },
    danger: { bg: '#dc3545', color: 'white', border: 'none' }
  };
  const style = variants[variant];
  
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '0.5rem 1rem',
        backgroundColor: disabled ? '#e9ecef' : style.bg,
        color: disabled ? '#6c757d' : style.color,
        border: style.border,
        borderRadius: '6px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontWeight: '500',
        fontSize: '0.9rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        transition: 'opacity 0.2s'
      }}
      onMouseEnter={(e) => !disabled && (e.currentTarget.style.opacity = '0.9')}
      onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
    >
      {icon && <span>{icon}</span>}
      {children}
    </button>
  );
});

const Modal = memo(({ isOpen, onClose, title, children, width = '600px' }) => {
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
    }
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;
  
  return ReactDOM.createPortal(
    <div 
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000
      }}
    >
      <div 
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          width: width,
          maxWidth: '95vw',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 50px rgba(0,0,0,0.3)'
        }}
      >
        <div style={{
          padding: '1rem 1.5rem',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#1f2937' }}>{title}</h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '0.25rem'
            }}
          >
            ×
          </button>
        </div>
        <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
});

const Toast = memo(({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const colors = {
    success: { bg: '#d4edda', color: '#155724', icon: '✓' },
    error: { bg: '#f8d7da', color: '#721c24', icon: '✕' },
    warning: { bg: '#fff3cd', color: '#856404', icon: '⚠' },
    info: { bg: '#cce5ff', color: '#004085', icon: 'ℹ' }
  };
  const style = colors[type];
  
  return (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      backgroundColor: style.bg,
      color: style.color,
      padding: '1rem 1.5rem',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      zIndex: 10001,
      animation: 'slideIn 0.3s ease'
    }}>
      <span style={{ fontSize: '1.2rem' }}>{style.icon}</span>
      <span>{message}</span>
      <button
        onClick={onClose}
        style={{
          background: 'none',
          border: 'none',
          color: style.color,
          cursor: 'pointer',
          fontSize: '1.2rem',
          marginLeft: '0.5rem'
        }}
      >
        ×
      </button>
    </div>
  );
});

// ============================================
// PDF HELPERS
// ============================================
const fmtCurrency = (value) => {
  const num = parseFloat(value) || 0;
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const pdfTableStyles = {
  theme: 'grid',
  styles: { fontSize: 8, cellPadding: { top: 3, right: 4, bottom: 3, left: 4 }, lineColor: [180, 180, 180], lineWidth: 0.3 },
  headStyles: { fillColor: [245, 245, 245], textColor: [0, 0, 0], fontStyle: 'bold', lineColor: [180, 180, 180] },
};

const drawPDFHeader = (doc, margin, contentWidth, jobNumber, jobName, billingLabel, entry, startY) => {
  let y = startY || 18;
  const col2 = margin + contentWidth * 0.6;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(billingLabel, margin, y); y += 6;

  const boxY = y;
  const rowH = 5.5;
  const lbl = (text, x, cy) => { doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.text(text, x, cy); };
  const val = (text, x, cy) => { doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.text(String(text || ''), x, cy); };

  lbl('Contract:', margin + 2, y); y += rowH;
  const contactName = (entry.opened_by || '').split(' - ')[0].trim();
  lbl('LGL Contact:', margin + 2, y); val(contactName, margin + 38, y); y += rowH;
  lbl('Change Order (By Client):', margin + 2, y); y += rowH;
  lbl('LGL Reference Number:', margin + 2, y); val(entry.new_extra_number || '', margin + 45, y);
  lbl('Approved By:', col2, y); y += rowH;
  lbl('Change Request Number:', margin + 2, y);
  lbl('Date:', col2, y); y += rowH;

  const printDate = new Date().toLocaleDateString('en-CA');
  lbl('Date Submitted:', col2, boxY);
  val(printDate, col2 + 30, boxY);

  doc.setDrawColor(180);
  doc.rect(margin, boxY - 4, contentWidth, y - boxY + 4);
  y += 6;
  return y;
};

// ============================================
// PDF GENERATION - Lump Sum
// ============================================
const generateLumpSumPDF = async (entry, jobNumber, jobName, branding = {}) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;

  const headerY = await drawLetterhead(doc, branding.resolved, {
    jobNumber,
    jobName,
    clientName: branding.clientName,
    projectType: branding.projectType,
    margin,
  });
  let y = drawPDFHeader(doc, margin, contentWidth, jobNumber, jobName, 'Lump Sum Price', entry, headerY);

  // Description
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
  doc.text('Description of change:', margin, y); y += 1;
  autoTable(doc, {
    startY: y,
    body: [[entry.description || '']],
    ...pdfTableStyles,
    styles: { ...pdfTableStyles.styles, minCellHeight: 14 },
    margin: { left: margin, right: margin },
  });
  y = doc.lastAutoTable.finalY + 4;

  // Drawing List
  const drawings = Array.isArray(entry.drawing_list) ? entry.drawing_list : [];
  if (drawings.length > 0) {
    autoTable(doc, {
      startY: y,
      head: [['Drawing/RFI List', 'Revision']],
      body: drawings.map(d => [d.reference || '', d.revision || '']),
      ...pdfTableStyles,
      margin: { left: margin, right: margin },
      columnStyles: { 0: { cellWidth: contentWidth * 0.7 }, 1: { cellWidth: contentWidth * 0.3 } },
    });
    y = doc.lastAutoTable.finalY + 2;
  }

  // RFI List
  const rfis = Array.isArray(entry.rfi_list) ? entry.rfi_list : [];
  if (rfis.length > 0) {
    autoTable(doc, {
      startY: y,
      head: [['Reference', 'Revision']],
      body: rfis.map(r => [r.reference || '', r.revision || '']),
      ...pdfTableStyles,
      margin: { left: margin, right: margin },
      columnStyles: { 0: { cellWidth: contentWidth * 0.7 }, 1: { cellWidth: contentWidth * 0.3 } },
    });
    y = doc.lastAutoTable.finalY + 4;
  }

  // Construction Costs header
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5);
  doc.text('CONSTRUCTION COSTS', margin, y); y += 2;

  // Per-line selling amounts for the CLIENT PDF. Supports both the cost-builder
  // shape (trades with ST/OT/DT; material/sub/equip as qty x unit_price + markup)
  // and legacy direct selling_cost lines. This PDF intentionally shows ONLY the
  // resulting selling figures — never rates, unit prices, hours breakdown, or
  // markup, which are internal-only details.
  const coMarkup = parseFloat(entry.markup_percentage) || 0;
  const markupLineSell = (item) => {
    if (item && item.selling_cost !== undefined && item.unit_price === undefined) return parseFloat(item.selling_cost) || 0;
    const base = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
    return base + (base * coMarkup / 100);
  };

  // Direct (Labour)
  const tmLabour = Array.isArray(entry.tm_labour_items) ? entry.tm_labour_items : [];
  const legacyLabour = Array.isArray(entry.labour_items) ? entry.labour_items : [];
  const usingTradeLabour = tmLabour.length > 0;
  const labourSrc = usingTradeLabour ? tmLabour : legacyLabour;
  const labourRowHours = (t) => usingTradeLabour
    ? (parseFloat(t.st_hours) || 0) + (parseFloat(t.ot_hours) || 0) + (parseFloat(t.dt_hours) || 0)
    : (parseFloat(t.hours) || 0);
  const labourRowSell = (t) => {
    if (usingTradeLabour) {
      const calcRow = (h, r) => (parseFloat(h) || 0) * (parseFloat(r) || 0);
      return calcRow(t.st_hours, t.st_rate) + calcRow(t.ot_hours, t.ot_rate) + calcRow(t.dt_hours, t.dt_rate);
    }
    return parseFloat(t.selling_cost) || 0;
  };
  const labourName = (t) => t.trade_name || t.description || '';

  const labourBody = labourSrc.length > 0
    ? labourSrc.map(t => [labourName(t), String(labourRowHours(t)), '$  ' + fmtCurrency(labourRowSell(t))])
    : [['', '', '']];

  const totalDirectHours = labourSrc.reduce((sum, t) => sum + labourRowHours(t), 0);

  autoTable(doc, {
    startY: y,
    head: [['Direct - Item Description', 'Hours', 'Selling Cost']],
    body: labourBody,
    ...pdfTableStyles,
    margin: { left: margin, right: margin },
    columnStyles: { 0: { cellWidth: contentWidth * 0.55 }, 1: { cellWidth: contentWidth * 0.15, halign: 'right' }, 2: { cellWidth: contentWidth * 0.3, halign: 'right' } },
  });
  y = doc.lastAutoTable.finalY;

  const labourTotalVal = parseFloat(entry.labour_total) || 0;
  autoTable(doc, {
    startY: y, body: [['Direct Total:', String(totalDirectHours), '$  ' + fmtCurrency(labourTotalVal)]],
    ...pdfTableStyles,
    styles: { ...pdfTableStyles.styles, fontStyle: 'bold' },
    margin: { left: margin, right: margin },
    columnStyles: { 0: { cellWidth: contentWidth * 0.55 }, 1: { cellWidth: contentWidth * 0.15, halign: 'right' }, 2: { cellWidth: contentWidth * 0.3, halign: 'right' } },
  });
  y = doc.lastAutoTable.finalY;

  // Material
  const materialItems = Array.isArray(entry.material_items) ? entry.material_items : [];
  const materialBody = materialItems.length > 0
    ? materialItems.map(item => [item.description || '', '$  ' + fmtCurrency(markupLineSell(item))])
    : [['', '']];

  autoTable(doc, {
    startY: y,
    head: [['Material Description', 'Selling Cost']],
    body: materialBody,
    ...pdfTableStyles,
    margin: { left: margin, right: margin },
    columnStyles: { 0: { cellWidth: contentWidth * 0.7 }, 1: { cellWidth: contentWidth * 0.3, halign: 'right' } },
  });
  y = doc.lastAutoTable.finalY;

  const matTotalVal = parseFloat(entry.material_total) || 0;
  autoTable(doc, {
    startY: y, body: [['Material Total:', '$  ' + fmtCurrency(matTotalVal)]],
    ...pdfTableStyles,
    styles: { ...pdfTableStyles.styles, fontStyle: 'bold' },
    margin: { left: margin, right: margin },
    columnStyles: { 0: { cellWidth: contentWidth * 0.7 }, 1: { cellWidth: contentWidth * 0.3, halign: 'right' } },
  });
  y = doc.lastAutoTable.finalY;

  // Subcontractors
  const subItems = Array.isArray(entry.subcontractor_items) ? entry.subcontractor_items : [];
  const subBody = subItems.length > 0
    ? subItems.map(item => [item.description || '', '$  ' + fmtCurrency(markupLineSell(item))])
    : [['', '']];

  autoTable(doc, {
    startY: y,
    head: [['Subcontractor Description', 'Selling Cost']],
    body: subBody,
    ...pdfTableStyles,
    margin: { left: margin, right: margin },
    columnStyles: { 0: { cellWidth: contentWidth * 0.7 }, 1: { cellWidth: contentWidth * 0.3, halign: 'right' } },
  });
  y = doc.lastAutoTable.finalY;

  const subTotalVal = parseFloat(entry.subcontractor_total) || 0;
  autoTable(doc, {
    startY: y, body: [['Subcontractor Total:', '$  ' + fmtCurrency(subTotalVal)]],
    ...pdfTableStyles,
    styles: { ...pdfTableStyles.styles, fontStyle: 'bold' },
    margin: { left: margin, right: margin },
    columnStyles: { 0: { cellWidth: contentWidth * 0.7 }, 1: { cellWidth: contentWidth * 0.3, halign: 'right' } },
  });
  y = doc.lastAutoTable.finalY;

  // Equipment
  const equipItems = Array.isArray(entry.equipment_items) ? entry.equipment_items : [];
  const equipBody = equipItems.length > 0
    ? equipItems.map(item => [item.description || '', '$  ' + fmtCurrency(markupLineSell(item))])
    : [['', '']];

  autoTable(doc, {
    startY: y,
    head: [['Equipment Description', 'Selling Cost']],
    body: equipBody,
    ...pdfTableStyles,
    margin: { left: margin, right: margin },
    columnStyles: { 0: { cellWidth: contentWidth * 0.7 }, 1: { cellWidth: contentWidth * 0.3, halign: 'right' } },
  });
  y = doc.lastAutoTable.finalY;

  const equipTotalVal = parseFloat(entry.equipment_total) || 0;
  const taxVal = parseFloat(entry.self_assessed_tax) || 0;
  const roomBoardVal = parseFloat(entry.room_and_board) || 0;
  const transportVal = parseFloat(entry.transport) || 0;
  const totalsBody = [['Equipment Total:', '$  ' + fmtCurrency(equipTotalVal)]];
  totalsBody.push(['Room & Board', '$  ' + fmtCurrency(roomBoardVal)]);
  totalsBody.push(['Transport', '$  ' + fmtCurrency(transportVal)]);
  totalsBody.push(['Self-Assessed Tax', '$  ' + fmtCurrency(taxVal)]);
  autoTable(doc, {
    startY: y,
    body: totalsBody,
    ...pdfTableStyles,
    styles: { ...pdfTableStyles.styles, fontStyle: 'bold' },
    margin: { left: margin, right: margin },
    columnStyles: { 0: { cellWidth: contentWidth * 0.7 }, 1: { cellWidth: contentWidth * 0.3, halign: 'right' } },
  });
  y = doc.lastAutoTable.finalY + 2;

  // Change Order Total = Direct + Material + Subcontractors + Equipment + Room & Board + Transport + Tax
  const coTotal = parseFloat(entry.change_order_total) || (labourTotalVal + matTotalVal + subTotalVal + equipTotalVal + roomBoardVal + transportVal + taxVal);
  const estHours = parseFloat(entry.estimated_hours) || totalDirectHours;
  autoTable(doc, {
    startY: y,
    body: [
      ['Total Estimated Hours', String(estHours)],
      ['Change Order Total', '$  ' + fmtCurrency(coTotal)],
    ],
    ...pdfTableStyles,
    styles: { ...pdfTableStyles.styles, fontSize: 10, fontStyle: 'bold', cellPadding: { top: 4, right: 5, bottom: 4, left: 5 } },
    margin: { left: margin, right: margin },
    columnStyles: { 0: { cellWidth: contentWidth * 0.7 }, 1: { cellWidth: contentWidth * 0.3, halign: 'right' } },
  });
  y = doc.lastAutoTable.finalY + 4;

  // Schedule Impact
  const shopDays = entry.schedule_shop_days ?? 'TBC';
  const fieldDays = entry.schedule_field_days ?? 'TBC';
  const totalDays = entry.schedule_total_days ?? 'TBC';
  autoTable(doc, {
    startY: y,
    head: [['Schedule Impact', '', '']],
    body: [
      ['Total (days):', 'Shop:', String(shopDays)],
      ['', 'Field:', String(fieldDays)],
      ['', 'Total:', String(totalDays)],
    ],
    ...pdfTableStyles,
    margin: { left: margin, right: margin },
  });
  y = doc.lastAutoTable.finalY + 4;

  // Clarification
  doc.setFontSize(7); doc.setFont('helvetica', 'italic');
  const clarText = 'Clarifications: ' + (entry.clarification || DEFAULT_CLARIFICATION);
  const clarLines = doc.splitTextToSize(clarText, contentWidth);
  doc.text(clarLines, margin, y);

  doc.save(exportFilename({
    job: jobNumber,
    reportSlug: `Change_Order_LumpSum_${entry.new_extra_number || 'draft'}`,
    ext: 'pdf',
  }));
};

// ============================================
// PDF GENERATION - T&M
// ============================================
const generateTMPDF = async (entry, jobNumber, jobName, branding = {}) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;
  const markup = parseFloat(entry.markup_percentage) || 8.75;

  const headerY = await drawLetterhead(doc, branding.resolved, {
    jobNumber,
    jobName,
    clientName: branding.clientName,
    projectType: branding.projectType,
    margin,
  });
  let y = drawPDFHeader(doc, margin, contentWidth, jobNumber, jobName, 'T&M Budget Price', entry, headerY);

  // Description
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
  doc.text('Description of change:', margin, y); y += 1;
  autoTable(doc, {
    startY: y,
    body: [[entry.description || '']],
    ...pdfTableStyles,
    styles: { ...pdfTableStyles.styles, minCellHeight: 14 },
    margin: { left: margin, right: margin },
  });
  y = doc.lastAutoTable.finalY + 4;

  // Drawing/RFI List
  const drawings = Array.isArray(entry.drawing_list) ? entry.drawing_list : [];
  const rfis = Array.isArray(entry.rfi_list) ? entry.rfi_list : [];
  const allDrawings = [...drawings, ...rfis];
  if (allDrawings.length > 0) {
    autoTable(doc, {
      startY: y,
      head: [['Drawing/RFI List', 'Revision']],
      body: allDrawings.map(d => [d.reference || '', d.revision || '']),
      ...pdfTableStyles,
      margin: { left: margin, right: margin },
    });
    y = doc.lastAutoTable.finalY + 4;
  }

  doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5);
  doc.text('DIRECT CONSTRUCTION COSTS', margin, y); y += 2;

  // ---- LABOUR (by Trade with ST/OT/DT) ----
  const tmTrades = Array.isArray(entry.tm_labour_items) ? entry.tm_labour_items : [];
  const calcRow = (h, r) => (parseFloat(h) || 0) * (parseFloat(r) || 0);

  const labourBody = [];
  const labourHead = [['Labour', '', 'Hours', 'Hourly Rate', '', 'Total', '']];
  tmTrades.forEach(trade => {
    labourBody.push([
      { content: trade.trade_name || 'Trade', styles: { fontStyle: 'bold' } },
      '',
      '', '', '', '$  ' + fmtCurrency(
        calcRow(trade.st_hours, trade.st_rate) + calcRow(trade.ot_hours, trade.ot_rate) + calcRow(trade.dt_hours, trade.dt_rate)
      ), ''
    ]);
    [
      { label: 'Straight Time', h: trade.st_hours, r: trade.st_rate },
      { label: 'Overtime', h: trade.ot_hours, r: trade.ot_rate },
      { label: 'Double Time', h: trade.dt_hours, r: trade.dt_rate },
    ].forEach(row => {
      labourBody.push([
        { content: '    ' + row.label, styles: { textColor: [100, 100, 100] } },
        '', String(row.h || 0),
        '$  ' + fmtCurrency(row.r || 0), '',
        '$  ' + fmtCurrency(calcRow(row.h, row.r)), ''
      ]);
    });
  });
  if (labourBody.length === 0) {
    labourBody.push([`${entry.discipline || 'Trade'} Field`, '', '0', '$  -', '', '$  -', '']);
  }

  autoTable(doc, {
    startY: y,
    head: labourHead,
    body: labourBody,
    ...pdfTableStyles,
    margin: { left: margin, right: margin },
    columnStyles: {
      0: { cellWidth: contentWidth * 0.28 },
      1: { cellWidth: contentWidth * 0.08, halign: 'center' },
      2: { cellWidth: contentWidth * 0.10, halign: 'right' },
      3: { cellWidth: contentWidth * 0.18, halign: 'right' },
      4: { cellWidth: contentWidth * 0.06 },
      5: { cellWidth: contentWidth * 0.18, halign: 'right' },
      6: { cellWidth: contentWidth * 0.12 },
    },
  });
  y = doc.lastAutoTable.finalY;

  const labourTotalVal = parseFloat(entry.labour_total) || tmTrades.reduce((s, t) =>
    s + calcRow(t.st_hours, t.st_rate) + calcRow(t.ot_hours, t.ot_rate) + calcRow(t.dt_hours, t.dt_rate), 0);
  autoTable(doc, {
    startY: y,
    body: [['Labour Total', '', '$  ' + fmtCurrency(labourTotalVal)]],
    ...pdfTableStyles,
    styles: { ...pdfTableStyles.styles, fontStyle: 'bold' },
    margin: { left: margin, right: margin },
    columnStyles: { 0: { cellWidth: contentWidth * 0.36 }, 1: { cellWidth: contentWidth * 0.34 }, 2: { cellWidth: contentWidth * 0.30, halign: 'right' } },
  });
  y = doc.lastAutoTable.finalY;

  // Helper for markup sections (Material, Subcontractors, Equipment)
  const renderMarkupSection = (title, items) => {
    const sectionItems = Array.isArray(items) ? items : [];
    const head = [[title, '', 'Quantity', 'Unit Price', 'Markup', 'Total', '']];
    const body = [];
    body.push(['', '', '', '', markup + '%', '$  -', '']);
    sectionItems.forEach(item => {
      const base = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
      const total = base + (base * markup / 100);
      body.push([
        item.description || '', '',
        String(item.quantity || ''),
        '$  ' + fmtCurrency(item.unit_price || 0),
        markup + '%',
        '$  ' + fmtCurrency(total), ''
      ]);
    });
    if (sectionItems.length === 0) {
      body.length = 0;
      body.push(['', '', '', '$  -', markup + '%', '$  -', '']);
    }

    autoTable(doc, {
      startY: y,
      head: head,
      body: body,
      ...pdfTableStyles,
      margin: { left: margin, right: margin },
      columnStyles: {
        0: { cellWidth: contentWidth * 0.28 },
        1: { cellWidth: contentWidth * 0.08 },
        2: { cellWidth: contentWidth * 0.10, halign: 'right' },
        3: { cellWidth: contentWidth * 0.18, halign: 'right' },
        4: { cellWidth: contentWidth * 0.06, halign: 'center', fontSize: 7 },
        5: { cellWidth: contentWidth * 0.18, halign: 'right' },
        6: { cellWidth: contentWidth * 0.12 },
      },
    });
    y = doc.lastAutoTable.finalY;

    const sectionTotal = sectionItems.reduce((s, item) => {
      const base = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
      return s + base + (base * markup / 100);
    }, 0);

    autoTable(doc, {
      startY: y,
      body: [[title + ' Total', '', '$  ' + fmtCurrency(sectionTotal)]],
      ...pdfTableStyles,
      styles: { ...pdfTableStyles.styles, fontStyle: 'bold' },
      margin: { left: margin, right: margin },
      columnStyles: { 0: { cellWidth: contentWidth * 0.36 }, 1: { cellWidth: contentWidth * 0.34 }, 2: { cellWidth: contentWidth * 0.30, halign: 'right' } },
    });
    y = doc.lastAutoTable.finalY;
  };

  // ---- MATERIAL ----
  renderMarkupSection('Material', entry.material_items);

  // ---- SUBCONTRACTORS ----
  renderMarkupSection('Subcontractors', entry.subcontractor_items);

  // ---- EQUIPMENT ----
  renderMarkupSection('Equipment', entry.equipment_items);

  // ---- ROOM & BOARD + TRANSPORT ----
  const rbVal = parseFloat(entry.room_and_board) || 0;
  const trVal = parseFloat(entry.transport) || 0;
  {
    const rbtBody = [
      ['Room & Board', 'Accommodation & Living Expenses', '$  ' + fmtCurrency(rbVal)],
      ['Transport', 'Travel & Transportation', '$  ' + fmtCurrency(trVal)],
    ];
    autoTable(doc, {
      startY: y,
      head: [['Other Costs', '', '']],
      body: rbtBody,
      ...pdfTableStyles,
      margin: { left: margin, right: margin },
      columnStyles: { 0: { cellWidth: contentWidth * 0.36 }, 1: { cellWidth: contentWidth * 0.34 }, 2: { cellWidth: contentWidth * 0.30, halign: 'right' } },
    });
    y = doc.lastAutoTable.finalY;
  }

  // Totals summary
  const coTotal = parseFloat(entry.change_order_total) || 0;
  const tmEstHours = tmTrades.reduce((s, t) =>
    s + (parseFloat(t.st_hours) || 0) + (parseFloat(t.ot_hours) || 0) + (parseFloat(t.dt_hours) || 0), 0);
  const tmTotalHours = parseFloat(entry.estimated_hours) || tmEstHours;
  autoTable(doc, {
    startY: y + 2,
    body: [
      ['Total Estimated Hours', String(tmTotalHours)],
      ['Change Order Total', '$  ' + fmtCurrency(coTotal)],
    ],
    ...pdfTableStyles,
    styles: { ...pdfTableStyles.styles, fontSize: 10, fontStyle: 'bold', cellPadding: { top: 4, right: 5, bottom: 4, left: 5 } },
    margin: { left: margin, right: margin },
    columnStyles: { 0: { cellWidth: contentWidth * 0.7 }, 1: { cellWidth: contentWidth * 0.3, halign: 'right' } },
  });
  y = doc.lastAutoTable.finalY + 4;

  // Schedule Impact
  const shopDays = entry.schedule_shop_days ?? 'TBC';
  const fieldDays = entry.schedule_field_days ?? 'TBC';
  const totalDays = entry.schedule_total_days ?? 'TBC';
  autoTable(doc, {
    startY: y,
    head: [['Schedule Impact', '', '']],
    body: [
      ['Total (days):', 'Shop:', String(shopDays)],
      ['', 'Field:', String(fieldDays)],
      ['', 'Total:', String(totalDays)],
    ],
    ...pdfTableStyles,
    margin: { left: margin, right: margin },
  });
  y = doc.lastAutoTable.finalY + 4;

  // Clarifications
  doc.setFontSize(7); doc.setFont('helvetica', 'italic');
  const clarText = 'Clarifications: ' + (entry.clarification || DEFAULT_CLARIFICATION);
  const clarLines = doc.splitTextToSize(clarText, contentWidth);
  doc.text(clarLines, margin, y);

  doc.save(exportFilename({
    job: jobNumber,
    reportSlug: `Change_Order_TM_${entry.new_extra_number || 'draft'}`,
    ext: 'pdf',
  }));
};

// ============================================
// MAIN COMPONENT
// ============================================
const ControlChartDashboard = ({ projectId, jobNumber: propJobNumber }) => {
  const { instance, accounts } = useMsal();

  // Live data push: ref + callback for real-time sync
  const fetchDataRef = useRef(null);
  const handleDataChange = useCallback(() => {
    if (fetchDataRef.current) fetchDataRef.current();
  }, []);

  const { viewers, isConnected, connectionState, socketId } = usePresence('controlChart', projectId || propJobNumber || 'global', true, handleDataChange);

  // Socket ID tracking for sync headers
  const socketIdRef = useRef(null);
  useEffect(() => { socketIdRef.current = socketId; }, [socketId]);

  const syncHeaders = useCallback(() => {
    return socketIdRef.current ? { headers: { 'x-socket-id': socketIdRef.current } } : {};
  }, []);
  
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [jobNumber, setJobNumber] = useState(propJobNumber || '');
  const [jobName, setJobName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewFilter, setViewFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const skipBlurSaveRef = useRef(false);
  const [toast, setToast] = useState(null);
  const [columnWidths, setColumnWidths] = useState(
    COLUMNS.reduce((acc, col) => ({ ...acc, [col.key]: col.width }), {})
  );
  const [widthHistory, setWidthHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [disciplineSummary, setDisciplineSummary] = useState([]);
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analyticsTypeFilter, setAnalyticsTypeFilter] = useState(() => new Set(['Lump Sum', 'T&M', 'Tracking Only', 'Material Supply Only']));
  const [analyticsStatusFilter, setAnalyticsStatusFilter] = useState(() => new Set(STATUS_OPTIONS));
  const [analyticsTab, setAnalyticsTab] = useState('overview');
  const [visibleColumns, setVisibleColumns] = useState(() => new Set(COLUMNS.map(c => c.key)));
  const { columnOrder, moveColumn } = useColumnOrder('control-chart-column-order', COLUMNS);
  const { frozenKeys, toggleFrozen, clearFrozen } = useFrozenColumns(
    'control-chart-frozen-columns',
    DEFAULT_FROZEN_KEYS,
    { max: MAX_FROZEN_COLUMNS, validKeys: COLUMNS.map(c => c.key) }
  );
  const orderedColumns = useMemo(() => applyColumnOrder(COLUMNS, columnOrder), [columnOrder]);
  const displayedColumns = useMemo(
    () => orderedColumns.filter(c => visibleColumns.has(c.key)),
    [orderedColumns, visibleColumns]
  );
  const tableColumns = useMemo(() => {
    const frozen = displayedColumns.filter(c => frozenKeys.has(c.key));
    const rest = displayedColumns.filter(c => !frozenKeys.has(c.key));
    return [...frozen, ...rest];
  }, [displayedColumns, frozenKeys]);
  const frozenLeftByKey = useMemo(() => {
    const map = { __checkbox: 0 };
    let left = CHECKBOX_COL_WIDTH;
    for (const col of tableColumns) {
      if (!frozenKeys.has(col.key)) break;
      map[col.key] = left;
      left += columnWidths[col.key] || col.width || 120;
    }
    return map;
  }, [tableColumns, frozenKeys, columnWidths]);
  const lastFrozenKey = useMemo(() => {
    const frozen = tableColumns.filter(c => frozenKeys.has(c.key));
    return frozen.length ? frozen[frozen.length - 1].key : '__checkbox';
  }, [tableColumns, frozenKeys]);
  const getFreezeStyle = useCallback((colKey, { isHeader = false, rowBg } = {}) => {
    const isFrozen = colKey === '__checkbox' || frozenKeys.has(colKey);
    if (!isFrozen) {
      return isHeader
        ? { position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#f8f9fa' }
        : {};
    }
    return {
      position: 'sticky',
      left: colKey === '__checkbox' ? 0 : frozenLeftByKey[colKey],
      ...(isHeader ? { top: 0 } : {}),
      zIndex: isHeader ? 12 : 5,
      backgroundColor: isHeader ? '#f8f9fa' : (rowBg || 'inherit'),
      boxShadow: colKey === lastFrozenKey ? '2px 0 6px rgba(15, 23, 42, 0.12)' : undefined,
    };
  }, [frozenKeys, frozenLeftByKey, lastFrozenKey]);
  
  const resizingRef = useRef(null);
  const columnsButtonRef = useRef(null);
  
  const currentUserName = useMemo(() => {
    const account = accounts?.[0];
    if (!account) return '';
    return account.name || account.username || '';
  }, [accounts]);
  
  const {
    filteredItems: filterHookResults,
    columnFilters,
    openFilterColumn,
    filterSearch,
    filterMode,
    filterText,
    filterButtonPositions,
    setOpenFilterColumn,
    setFilterSearch,
    getUniqueValues,
    handleFilterToggle,
    selectAllColumnFilter,
    clearColumnFilter,
    applyTextFilter,
    clearAllFilters,
    getActiveFilters,
    openFilterAtPosition,
    numericColumnKeys,
    dateColumnKeys,
    columnLabels,
  } = useFilterTemplate({ items: data, columns: COLUMNS, numericColumnKeys: NUMERIC_COLUMN_KEYS });
  
  const fetchData = useCallback(async () => {
    if (!projectId) return;
    
    setLoading(true);
    try {
      const [dataResult, summaryResult, configResult] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', `/control-chart/${projectId}`),
        authenticatedApiCall(instance, accounts, 'GET', `/control-chart/${projectId}/summary`),
        authenticatedApiCall(instance, accounts, 'GET', `/project-configuration/${projectId}`)
      ]);
      
      if (dataResult.data?.success) {
        setData(dataResult.data.data);
        setJobNumber(dataResult.data.jobNumber || propJobNumber);
        setJobName(dataResult.data.jobName || '');
      }
      
      if (summaryResult.data?.success) {
        setSummary(summaryResult.data.data);
        setDisciplineSummary(summaryResult.data.disciplineSummary || []);
      }

      if (configResult.data?.success) {
        setConfig(configResult.data.data || DEFAULT_CONFIG);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching control chart data:', err);
      setError('Failed to load control chart data');
    } finally {
      setLoading(false);
    }
  }, [projectId, propJobNumber, instance, accounts]);

  useEffect(() => { fetchDataRef.current = fetchData; }, [fetchData]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const autoFitOnLoad = useRef(false);
  useEffect(() => {
    if (data.length > 0 && !autoFitOnLoad.current) {
      autoFitOnLoad.current = true;
      setTimeout(() => autoFitColumns(), 100);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  
  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  const handleSaveConfig = useCallback(async (values) => {
    if (!projectId) return;
    try {
      const res = await authenticatedApiCall(instance, accounts, 'PUT', `/project-configuration/${projectId}`, values);
      if (res.data?.success) {
        setConfig(res.data.data || values);
        showToast('Configuration saved');
        setShowConfigModal(false);
      } else {
        showToast('Failed to save configuration', 'error');
      }
    } catch (err) {
      console.error('Error saving project configuration:', err);
      showToast('Failed to save configuration', 'error');
    }
  }, [projectId, instance, accounts, showToast]);
  
  // Per discipline counts (from the full dataset) for the View dropdown
  const NO_DISC_ID = '__nodisc__';
  const disciplineColorMap = useMemo(() => {
    const map = {};
    DISCIPLINES.forEach((d, i) => { map[d] = DISCIPLINE_PALETTE[i % DISCIPLINE_PALETTE.length]; });
    return map;
  }, []);

  const viewDisciplineCounts = useMemo(() => {
    const counts = { all: data.length, [NO_DISC_ID]: 0 };
    data.forEach(row => {
      const d = row.discipline;
      if (!d) counts[NO_DISC_ID] += 1;
      else counts[d] = (counts[d] || 0) + 1;
    });
    return counts;
  }, [data]);

  const viewOptions = useMemo(() => {
    const opts = [{ id: 'all', label: 'All Disciplines', count: viewDisciplineCounts.all, color: '#6b7280' }];
    // Known disciplines first (in coding-structure order), then any unexpected ones
    DISCIPLINES.forEach(d => {
      if ((viewDisciplineCounts[d] || 0) > 0) {
        opts.push({ id: d, label: d, count: viewDisciplineCounts[d], color: disciplineColorMap[d] || '#6b7280' });
      }
    });
    Object.keys(viewDisciplineCounts).forEach(d => {
      if (d === 'all' || d === NO_DISC_ID || DISCIPLINES.includes(d)) return;
      opts.push({ id: d, label: d, count: viewDisciplineCounts[d], color: '#6b7280' });
    });
    if (viewDisciplineCounts[NO_DISC_ID] > 0) {
      opts.push({ id: NO_DISC_ID, label: 'Unassigned', count: viewDisciplineCounts[NO_DISC_ID], color: '#9ca3af' });
    }
    return opts;
  }, [viewDisciplineCounts, disciplineColorMap]);

  const filteredData = useMemo(() => {
    let result = [...filterHookResults];

    if (viewFilter !== 'all') {
      if (viewFilter === NO_DISC_ID) {
        result = result.filter(row => !row.discipline);
      } else {
        result = result.filter(row => row.discipline === viewFilter);
      }
    }
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(row =>
        Object.values(row).some(val =>
          String(val || '').toLowerCase().includes(search)
        )
      );
    }
    
    if (sortConfig.key) {
      result.sort((a, b) => {
        const aVal = a[sortConfig.key] || '';
        const bVal = b[sortConfig.key] || '';
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return result;
  }, [filterHookResults, viewFilter, searchTerm, sortConfig]);
  
  const handleSort = useCallback((columnKey) => {
    setSortConfig(prev => ({
      key: columnKey,
      direction: prev.key === columnKey && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);
  
  const handleSelectRow = useCallback((id) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  
  const handleSelectAll = useCallback((checked) => {
    if (checked) {
      setSelectedRows(new Set(filteredData.map(d => d.id)));
    } else {
      setSelectedRows(new Set());
    }
  }, [filteredData]);
  
  const handleResizeStart = useCallback((e, columnKey) => {
    e.preventDefault();
    resizingRef.current = { columnKey, startX: e.clientX, startWidth: columnWidths[columnKey] };
    
    const handleMove = (e) => {
      if (!resizingRef.current) return;
      const diff = e.clientX - resizingRef.current.startX;
      const newWidth = Math.max(60, resizingRef.current.startWidth + diff);
      setColumnWidths(prev => ({ ...prev, [resizingRef.current.columnKey]: newWidth }));
    };
    
    const handleUp = () => {
      resizingRef.current = null;
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
    
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
  }, [columnWidths]);

  const formatCellValue = useCallback((value, type) => {
    if (value === null || value === undefined) return '-';
    switch (type) {
      case 'currency':
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
      case 'date':
        return value ? new Date(value).toLocaleDateString() : '-';
      case 'number':
        return typeof value === 'number' ? value.toLocaleString() : value;
      default:
        return String(value);
    }
  }, []);

  const saveWidthHistory = useCallback(() => {
    setWidthHistory(prev => [...prev.slice(-19), { ...columnWidths }]);
  }, [columnWidths]);

  const autoFitColumns = useCallback(() => {
    saveWidthHistory();
    const CELL_PAD = 24;
    const HEADER_PAD = 40;
    const MIN_W = 60;
    const MAX_W = 400;
    const HEADER_FONT = 'bold 13px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    const CELL_FONT = '13px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    const cols = displayedColumns;
    const sampled = filteredData.length > 200 ? filteredData.slice(0, 200) : filteredData;
    const newWidths = {};
    cols.forEach(col => {
      let maxW = measureTextWidth(col.label, HEADER_FONT) + HEADER_PAD;
      for (const row of sampled) {
        const val = formatCellValue(row[col.key], col.type);
        const w = measureTextWidth(String(val), CELL_FONT) + CELL_PAD;
        if (w > maxW) maxW = w;
      }
      newWidths[col.key] = Math.max(MIN_W, Math.min(MAX_W, Math.ceil(maxW)));
    });
    setColumnWidths(prev => ({ ...prev, ...newWidths }));
  }, [saveWidthHistory, displayedColumns, filteredData, formatCellValue]);

  const resetColumnWidths = useCallback(() => {
    saveWidthHistory();
    setColumnWidths(COLUMNS.reduce((acc, col) => ({ ...acc, [col.key]: col.width }), {}));
  }, [saveWidthHistory]);

  const undoColumnWidths = useCallback(() => {
    if (widthHistory.length === 0) return;
    const prev = widthHistory[widthHistory.length - 1];
    setWidthHistory(h => h.slice(0, -1));
    setColumnWidths(prev);
  }, [widthHistory]);

  const handleAdd = useCallback(async (formData) => {
    try {
      const result = await authenticatedApiCall(instance, accounts, 'POST', `/control-chart`, { ...formData, job_number: jobNumber }, syncHeaders());
      
      if (result.data?.success) {
        const sync = result.data.extraSync;
        if (sync && !sync.skipped) {
          showToast(`Saved. Extra ${sync.opCode} is now on Time Tracker → Admin → Extras (${sync.disciplineCode}).`);
        } else if (sync?.skipped) {
          showToast(`Saved. Extra did not reach Time Tracker (${sync.skipped}).`, 'warning');
        } else {
          showToast('Entry added successfully');
        }
        setShowAddModal(false);
        fetchData();
      }
    } catch (err) {
      showToast('Failed to add entry', 'error');
    }
  }, [jobNumber, showToast, fetchData, instance, accounts]);
  
  const handleEdit = useCallback(async (formData) => {
    try {
      const result = await authenticatedApiCall(instance, accounts, 'PUT', `/control-chart/${editingEntry.id}`, formData, syncHeaders());
      
      if (result.data?.success) {
        const sync = result.data.extraSync;
        if (sync && !sync.skipped) {
          showToast(`Updated. Extra ${sync.opCode} is on Time Tracker → Admin → Extras (${sync.disciplineCode}).`);
        } else if (sync?.skipped) {
          showToast(`Updated. Extra did not reach Time Tracker (${sync.skipped}).`, 'warning');
        } else {
          showToast('Entry updated successfully');
        }
        setShowEditModal(false);
        setEditingEntry(null);
        fetchData();
      }
    } catch (err) {
      showToast('Failed to update entry', 'error');
    }
  }, [editingEntry, showToast, fetchData, instance, accounts]);
  
  const handleDelete = useCallback(async (id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    
    try {
      const result = await authenticatedApiCall(instance, accounts, 'DELETE', `/control-chart/${id}`, null, syncHeaders());
      
      if (result.data?.success) {
        showToast('Entry deleted successfully');
        fetchData();
      }
    } catch (err) {
      showToast('Failed to delete entry', 'error');
    }
  }, [showToast, fetchData, instance, accounts]);
  
  const handleBulkDelete = useCallback(async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedRows.size} entries?`)) return;
    
    try {
      const result = await authenticatedApiCall(instance, accounts, 'DELETE', `/control-chart/bulk/delete`, { ids: Array.from(selectedRows) }, syncHeaders());
      
      if (result.data?.success) {
        showToast(`Deleted ${result.data.deletedCount} entries`);
        setSelectedRows(new Set());
        fetchData();
      }
    } catch (err) {
      showToast('Failed to delete entries', 'error');
    }
  }, [selectedRows, showToast, fetchData, instance, accounts]);
  
  const handleDeleteSelected = handleBulkDelete;

  const handleToggleCancelSelected = useCallback(async () => {
    const selectedIds = [...selectedRows];
    const selectedEntries = data.filter(r => selectedIds.includes(r.id));
    const allCancelled = selectedEntries.every(r => getEntryStatus(r) === 'cancelled');
    const actionWord = allCancelled ? 'restore' : 'cancel';
    
    if (!window.confirm(`Are you sure you want to ${actionWord} ${selectedRows.size} entr${selectedRows.size === 1 ? 'y' : 'ies'}?`)) return;

    try {
      let successCount = 0;
      for (const id of selectedIds) {
        const row = data.find(r => r.id === id);
        if (!row) continue;
        // Restoring returns the entry to its natural lifecycle status; cancelling forces 'cancelled'.
        const newStatus = allCancelled ? deriveStatus(row) : 'cancelled';
        const payload = { ...row, status: newStatus };
        const result = await authenticatedApiCall(instance, accounts, 'PUT', `/control-chart/${id}`, payload, syncHeaders());
        if (result.data?.success) successCount++;
      }
      showToast(`${successCount} entr${successCount === 1 ? 'y' : 'ies'} ${actionWord === 'cancel' ? 'cancelled' : 'restored'}`);
      setSelectedRows(new Set());
      fetchData();
    } catch (err) {
      showToast(`Failed to ${actionWord} entries`, 'error');
    }
  }, [selectedRows, data, showToast, fetchData, instance, accounts, syncHeaders]);

  const handleInlineMaestroUpdate = useCallback(async (id, newStatus) => {
    try {
      const row = data.find(r => r.id === id);
      const payload = row ? { ...row, maestro_status: newStatus || null } : { maestro_status: newStatus || null };
      const result = await authenticatedApiCall(instance, accounts, 'PUT', `/control-chart/${id}`, payload, syncHeaders());
      if (result.data?.success) {
        setData(prev => prev.map(r => r.id === id ? { ...r, maestro_status: newStatus || null } : r));
        showToast(`Maestro updated to "${newStatus || '-'}"`);
      }
    } catch (err) {
      showToast('Failed to update Maestro status', 'error');
    }
  }, [instance, accounts, data, showToast]);

  // PUT replaces the whole row — always send the existing entry plus the one field.
  const handleInlineCellSave = useCallback(async (row, col, rawValue) => {
    const next = coerceInlineValue(col, rawValue);
    if (inlineValuesEqual(col, row[col.key], next)) {
      setEditingCell((prev) => (prev?.rowId === row.id && prev?.field === col.key ? null : prev));
      return;
    }
    const payload = { ...row, [col.key]: next };
    if ((col.key === 'approved_price' || col.key === 'estimated_price')
        && row.status !== 'cancelled' && row.status !== 'rejected') {
      payload.status = deriveStatus(payload);
    }
    try {
      const result = await authenticatedApiCall(instance, accounts, 'PUT', `/control-chart/${row.id}`, payload, syncHeaders());
      if (result.data?.success) {
        const updated = result.data.data || payload;
        setData((prev) => prev.map((r) => (r.id === row.id ? { ...r, ...updated } : r)));
        showToast(`${col.label} saved`);
      }
    } catch (err) {
      showToast(`Failed to update ${col.label}`, 'error');
    }
    setEditingCell((prev) => (prev?.rowId === row.id && prev?.field === col.key ? null : prev));
  }, [instance, accounts, showToast]);
  
  const handleExportExcel = useCallback(() => {
    if (filteredData.length === 0) {
      showToast('No data to export', 'info');
      return;
    }
    
    const visibleCols = displayedColumns;
    const csvContent = [
      visibleCols.map(c => c.label).join(','),
      ...filteredData.map(row => 
        visibleCols.map(c => {
          const val = row[c.key];
          if (val === null || val === undefined) return '';
          const strVal = String(val);
          if (strVal.includes(',') || strVal.includes('"') || strVal.includes('\n')) {
            return `"${strVal.replace(/"/g, '""')}"`;
          }
          return strVal;
        }).join(',')
      )
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `control_chart_${jobNumber}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showToast('Exported to CSV successfully');
  }, [filteredData, displayedColumns, jobNumber, showToast]);
  
  const handlePrintPDF = useCallback(async (entry) => {
    try {
      const resolved = await loadLetterhead({ instance, accounts, jobNumber });
      const branding = {
        resolved,
        clientName: resolved?.context?.client_name,
        projectType: resolved?.context?.project_type,
      };
      if (entry.billing_type === 'T&M') {
        await generateTMPDF(entry, jobNumber, jobName, branding);
      } else {
        await generateLumpSumPDF(entry, jobNumber, jobName, branding);
      }
      showToast('PDF generated and downloaded');
    } catch (err) {
      console.error('PDF generation error:', err);
      showToast('Failed to generate PDF', 'error');
    }
  }, [jobNumber, jobName, showToast, instance, accounts]);
  
  const analyticsData = useMemo(() => {
    const filtered = data.filter(row =>
      analyticsTypeFilter.has(row.billing_type || '') &&
      analyticsStatusFilter.has(getEntryStatus(row))
    );
    const num = (v) => parseFloat(v) || 0;

    const statusCounts = STATUS_OPTIONS.reduce((acc, s) => ({ ...acc, [s]: 0 }), {});
    filtered.forEach(r => { statusCounts[getEntryStatus(r)] += 1; });

    const totals = {
      total_entries: filtered.length,
      total_estimated_hours: filtered.reduce((s, r) => s + num(r.estimated_hours), 0),
      total_estimated_price: filtered.reduce((s, r) => s + num(r.estimated_price), 0),
      total_submitted_hours: filtered.reduce((s, r) => s + num(r.submitted_hours), 0),
      total_submitted: filtered.reduce((s, r) => s + num(r.submitted_price), 0),
      total_approved: filtered.reduce((s, r) => s + num(r.approved_price), 0),
      approved_count: filtered.filter(r => num(r.approved_price) > 0).length,
      total_room_and_board: filtered.reduce((s, r) => s + num(r.room_and_board), 0),
      total_transport: filtered.reduce((s, r) => s + num(r.transport), 0),
      lump_sum_count: filtered.filter(r => r.billing_type === 'Lump Sum').length,
      tm_count: filtered.filter(r => r.billing_type === 'T&M').length,
      tracking_only_count: filtered.filter(r => r.billing_type === 'Tracking Only').length,
      material_supply_count: filtered.filter(r => r.billing_type === 'Material Supply Only').length,
      cancelled_count: statusCounts.cancelled,
      rejected_count: statusCounts.rejected,
      ...statusCounts,
    };

    // Value-stream comparison (Estimated → Submitted → Approved)
    const valueComparison = [
      { name: 'Estimated', value: totals.total_estimated_price, fill: '#8b5cf6' },
      { name: 'Submitted', value: totals.total_submitted, fill: '#f59e0b' },
      { name: 'Approved', value: totals.total_approved, fill: '#10b981' },
    ];
    totals.approval_rate = totals.total_submitted > 0
      ? Math.round((totals.total_approved / totals.total_submitted) * 100)
      : 0;
    totals.estimate_accuracy = totals.total_estimated_price > 0
      ? Math.round((totals.total_submitted / totals.total_estimated_price) * 100)
      : 0;

    // By change type
    const byType = BILLING_TYPE_OPTIONS.map(t => {
      const rows = filtered.filter(r => r.billing_type === t);
      const tc = CHANGE_TYPE_COLORS[t] || {};
      return {
        type: t,
        name: t,
        count: rows.length,
        estimated_price: rows.reduce((s, r) => s + num(r.estimated_price), 0),
        submitted_price: rows.reduce((s, r) => s + num(r.submitted_price), 0),
        approved_price: rows.reduce((s, r) => s + num(r.approved_price), 0),
        color: tc.text || '#6b7280',
      };
    });
    const noType = filtered.filter(r => !r.billing_type);
    if (noType.length) {
      byType.push({ type: 'Unspecified', name: 'Unspecified', count: noType.length,
        estimated_price: noType.reduce((s, r) => s + num(r.estimated_price), 0),
        submitted_price: noType.reduce((s, r) => s + num(r.submitted_price), 0),
        approved_price: noType.reduce((s, r) => s + num(r.approved_price), 0), color: '#9ca3af' });
    }

    // By status
    const byStatus = STATUS_OPTIONS.map(s => {
      const rows = filtered.filter(r => getEntryStatus(r) === s);
      return {
        status: s,
        name: STATUS_META[s].label,
        count: rows.length,
        estimated_price: rows.reduce((acc, r) => acc + num(r.estimated_price), 0),
        submitted_price: rows.reduce((acc, r) => acc + num(r.submitted_price), 0),
        approved_price: rows.reduce((acc, r) => acc + num(r.approved_price), 0),
        color: STATUS_META[s].dot,
      };
    });

    // By discipline
    const byDiscipline = {};
    filtered.forEach(r => {
      const disc = r.discipline || 'Unassigned';
      if (!byDiscipline[disc]) {
        byDiscipline[disc] = { discipline: disc, name: disc, entry_count: 0, estimated_hours: 0, estimated_price: 0, submitted_hours: 0, submitted_price: 0, approved_price: 0, room_and_board: 0, transport: 0, lump_sum_count: 0, tm_count: 0, tracking_only_count: 0, material_supply_count: 0, cancelled_count: 0 };
      }
      const d = byDiscipline[disc];
      d.entry_count++;
      d.estimated_hours += num(r.estimated_hours);
      d.estimated_price += num(r.estimated_price);
      d.submitted_hours += num(r.submitted_hours);
      d.submitted_price += num(r.submitted_price);
      d.approved_price += num(r.approved_price);
      d.room_and_board += num(r.room_and_board);
      d.transport += num(r.transport);
      if (r.billing_type === 'Lump Sum') d.lump_sum_count++;
      if (r.billing_type === 'T&M') d.tm_count++;
      if (r.billing_type === 'Tracking Only') d.tracking_only_count++;
      if (r.billing_type === 'Material Supply Only') d.material_supply_count++;
      if (getEntryStatus(r) === 'cancelled') d.cancelled_count++;
    });
    const disciplines = Object.values(byDiscipline).sort((a, b) => b.entry_count - a.entry_count);

    // Monthly trend by commencement date (cumulative submitted vs approved)
    const monthMap = {};
    filtered.forEach(r => {
      const d = r.commencement_date ? new Date(r.commencement_date) : (r.created_at ? new Date(r.created_at) : null);
      if (!d || isNaN(d)) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!monthMap[key]) monthMap[key] = { month: key, count: 0, submitted_price: 0, approved_price: 0, estimated_price: 0 };
      monthMap[key].count++;
      monthMap[key].submitted_price += num(r.submitted_price);
      monthMap[key].approved_price += num(r.approved_price);
      monthMap[key].estimated_price += num(r.estimated_price);
    });
    const monthly = Object.values(monthMap).sort((a, b) => a.month.localeCompare(b.month));
    let cumSub = 0, cumApp = 0;
    monthly.forEach(m => { cumSub += m.submitted_price; cumApp += m.approved_price; m.cumulative_submitted = cumSub; m.cumulative_approved = cumApp; });

    return { totals, disciplines, byType, byStatus, valueComparison, monthly };
  }, [data, analyticsTypeFilter, analyticsStatusFilter]);

  const activeFiltersCount = getActiveFilters().length;
  
  if (loading) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center' }}>
        <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
        <p style={{ color: '#6b7280' }}>Loading control chart data...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center' }}>
        <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>❌</div>
        <p style={{ color: '#dc3545' }}>{error}</p>
        <Button onClick={fetchData}>Retry</Button>
      </div>
    );
  }
  
  return (
    <div style={{ padding: '1.5rem', backgroundColor: '#f8f9fa', minHeight: '100%' }}>
      {/* TOOLBAR */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '1rem',
        marginBottom: '1rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <input
            type="text"
            placeholder="🔍 Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '0.9rem',
              width: '250px'
            }}
          />

          <ChangeTypeViewDropdown
            value={viewFilter}
            onChange={setViewFilter}
            options={viewOptions}
          />

          {activeFiltersCount > 0 && (
            <span
              onClick={clearAllFilters}
              style={{
                backgroundColor: '#dc3545',
                color: 'white',
                padding: '0.25rem 0.75rem',
                borderRadius: '20px',
                fontSize: '0.8rem',
                cursor: 'pointer'
              }}
              title="Click to clear all filters"
            >
              {activeFiltersCount} filter{activeFiltersCount > 1 ? 's' : ''} active ✕
            </span>
          )}
        </div>
        
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', position: 'relative' }}>
          <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
          
          {selectedRows.size > 0 && (
            <>
              <span style={{ fontSize: '0.8rem', color: '#374151', fontWeight: '600', padding: '0.4rem 0.5rem', backgroundColor: '#e0f2fe', borderRadius: '4px' }}>
                {selectedRows.size} selected
              </span>
              {selectedRows.size === 1 && (
                <>
                  <Button onClick={() => {
                    const selectedId = [...selectedRows][0];
                    const row = data.find(r => r.id === selectedId);
                    if (row) { setEditingEntry(row); setShowEditModal(true); }
                  }} icon="✏️">Edit</Button>
                  <Button onClick={() => {
                    const selectedId = [...selectedRows][0];
                    const row = data.find(r => r.id === selectedId);
                    if (row) handlePrintPDF(row);
                  }} icon="📄">PDF</Button>
                </>
              )}
              {selectedRows.size > 1 && (
                <Button onClick={() => {
                  const selectedIds = [...selectedRows];
                  selectedIds.forEach(id => {
                    const row = data.find(r => r.id === id);
                    if (row) handlePrintPDF(row);
                  });
                }} icon="📄">PDF ({selectedRows.size})</Button>
              )}
              <Button onClick={() => handleToggleCancelSelected()} icon="🚫">
                Cancel/Restore
              </Button>
              <Button onClick={handleDeleteSelected} icon="🗑️">
                Delete ({selectedRows.size})
              </Button>
              <div style={{ width: '1px', height: '24px', backgroundColor: '#d1d5db' }} />
            </>
          )}
          
          <div style={{ position: 'relative' }} ref={columnsButtonRef}>
            <Button onClick={() => setShowColumnConfig(!showColumnConfig)} icon="📊">Columns</Button>
          </div>
          <Button onClick={() => setShowAddModal(true)} icon="➕">Add New</Button>
          <Button onClick={() => setShowConfigModal(true)} icon="⚙️">Configuration</Button>
          <Button onClick={autoFitColumns} icon="↔️">Auto-fit</Button>
          <Button onClick={resetColumnWidths} icon="↩️">Reset</Button>
          {widthHistory.length > 0 && (
            <Button onClick={undoColumnWidths} icon="⤺">Undo</Button>
          )}
          <Button onClick={() => showToast('Import feature coming soon', 'info')} icon="📥">Import</Button>
          <Button onClick={handleExportExcel} icon="📤">Export</Button>
          <Button onClick={() => setShowAnalytics(!showAnalytics)} icon="📈" variant={showAnalytics ? 'primary' : 'default'}>
            Analytics
          </Button>
          <Button onClick={() => setShowHelpModal(true)} icon="❓">Help</Button>
        </div>
      </div>
      
      {/* Column Config Dropdown - positioned relative to the button */}
      {showColumnConfig && ReactDOM.createPortal(
        <div
          onClick={() => setShowColumnConfig(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9998
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ 
              position: 'absolute', 
              top: columnsButtonRef.current ? columnsButtonRef.current.getBoundingClientRect().bottom + 5 : 220,
              left: columnsButtonRef.current ? Math.min(columnsButtonRef.current.getBoundingClientRect().left, window.innerWidth - 360) : 100,
              backgroundColor: 'white', 
              borderRadius: '8px', 
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)', 
              padding: '12px', 
              width: '380px', 
              maxHeight: '440px', 
              overflowY: 'auto' 
            }}
          >
            <div style={{ fontWeight: '600', marginBottom: '8px', color: '#374151', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
              <span>Columns ({visibleColumns.size})</span>
              <button onClick={() => setShowColumnConfig(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' }}>×</button>
            </div>
            <ColumnConfigPanel
              columns={orderedColumns}
              isVisible={(key) => visibleColumns.has(key)}
              onToggle={(key) => {
                setVisibleColumns(prev => {
                  const next = new Set(prev);
                  if (next.has(key)) { if (next.size > 1) next.delete(key); }
                  else next.add(key);
                  return next;
                });
              }}
              onMove={moveColumn}
              onShowAll={() => setVisibleColumns(new Set(COLUMNS.map(c => c.key)))}
              onHideAll={() => setVisibleColumns(new Set([COLUMNS[0].key]))}
              isFrozen={(key) => frozenKeys.has(key)}
              onToggleFreeze={toggleFrozen}
              onClearFrozen={clearFrozen}
              maxFrozen={MAX_FROZEN_COLUMNS}
            />
          </div>
        </div>,
        document.body
      )}
      
      {/* Analytics Modal */}
      {showAnalytics && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            width: '95%',
            maxWidth: '1000px',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
          }}>
            <div style={{
              padding: '1.5rem',
              borderBottom: '1px solid #dee2e6',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#f8f9fa'
            }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#333' }}>
                📊 Control Chart Analytics
              </h2>
              <button
                onClick={() => setShowAnalytics(false)}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Close
              </button>
            </div>

            {/* Slicer Bar */}
            <div style={{ padding: '0.75rem 1.5rem', borderBottom: '1px solid #e5e7eb', backgroundColor: '#fafbfc', display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#64748b', whiteSpace: 'nowrap' }}>Change Type:</span>
                {BILLING_TYPE_OPTIONS.map(type => {
                  const active = analyticsTypeFilter.has(type);
                  const tc = CHANGE_TYPE_COLORS[type] || {};
                  return (
                    <button
                      key={type}
                      onClick={() => setAnalyticsTypeFilter(prev => {
                        const next = new Set(prev);
                        if (next.has(type)) next.delete(type); else next.add(type);
                        return next;
                      })}
                      style={{
                        padding: '4px 10px',
                        borderRadius: '14px',
                        fontSize: '0.78rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        border: active ? `2px solid ${tc.text || '#374151'}` : '2px solid #d1d5db',
                        backgroundColor: active ? (tc.bg || '#f3f4f6') : 'white',
                        color: active ? (tc.text || '#374151') : '#9ca3af',
                        opacity: active ? 1 : 0.6,
                        transition: 'all 0.15s',
                      }}
                    >
                      {type}
                    </button>
                  );
                })}
              </div>
              <div style={{ width: '1px', height: '24px', backgroundColor: '#d1d5db' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#64748b', whiteSpace: 'nowrap' }}>Status:</span>
                {STATUS_OPTIONS.map(key => {
                  const meta = STATUS_META[key];
                  const active = analyticsStatusFilter.has(key);
                  return (
                    <button
                      key={key}
                      onClick={() => setAnalyticsStatusFilter(prev => {
                        const next = new Set(prev);
                        if (next.has(key)) next.delete(key); else next.add(key);
                        return next;
                      })}
                      style={{
                        padding: '4px 10px',
                        borderRadius: '14px',
                        fontSize: '0.78rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        border: active ? `2px solid ${meta.dot}` : '2px solid #d1d5db',
                        backgroundColor: active ? meta.badgeBg : 'white',
                        color: active ? meta.badgeText : '#9ca3af',
                        opacity: active ? 1 : 0.6,
                        transition: 'all 0.15s',
                      }}
                    >
                      {meta.label}
                    </button>
                  );
                })}
              </div>
              {(analyticsTypeFilter.size < 4 || analyticsStatusFilter.size < STATUS_OPTIONS.length) && (
                <button
                  onClick={() => { setAnalyticsTypeFilter(new Set(BILLING_TYPE_OPTIONS)); setAnalyticsStatusFilter(new Set(STATUS_OPTIONS)); }}
                  style={{ padding: '4px 10px', borderRadius: '14px', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer', border: '1px solid #d1d5db', backgroundColor: 'white', color: '#6b7280' }}
                >
                  Reset All
                </button>
              )}
            </div>
            
            {/* Analytics tab bar */}
            <div style={{ display: 'flex', gap: '0.25rem', padding: '0 1.5rem', borderBottom: '1px solid #e5e7eb', backgroundColor: '#fff', flexWrap: 'wrap' }}>
              {[
                { id: 'overview', label: 'Overview', icon: '📋' },
                { id: 'type', label: 'By Type', icon: '🏷️' },
                { id: 'discipline', label: 'By Discipline', icon: '🛠️' },
                { id: 'status', label: 'By Status', icon: '🚦' },
                { id: 'trends', label: 'Trends', icon: '📈' },
              ].map(t => (
                <button key={t.id} onClick={() => setAnalyticsTab(t.id)} style={{
                  padding: '0.6rem 1rem', border: 'none', background: 'none', cursor: 'pointer',
                  fontSize: '0.85rem', fontWeight: analyticsTab === t.id ? '700' : '500',
                  color: analyticsTab === t.id ? '#1e40af' : '#64748b',
                  borderBottom: analyticsTab === t.id ? '3px solid #3b82f6' : '3px solid transparent',
                }}>{t.icon} {t.label}</button>
              ))}
            </div>

            <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1, backgroundColor: '#f8fafc' }}>
              {analyticsData.totals.total_entries === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                  <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>📭</div>
                  <div>No entries match the current filter. Adjust the slicers above.</div>
                </div>
              ) : (() => {
                const A = analyticsData;
                const fmtMoney = (v) => `$${Math.round(Number(v) || 0).toLocaleString()}`;
                const card = { backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' };
                const cardTitle = { margin: '0 0 0.75rem 0', fontSize: '0.95rem', fontWeight: '700', color: '#1e293b' };
                const grid2 = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '1rem' };
                const axisTick = { fontSize: 11, fill: '#64748b' };
                const moneyAxis = (v) => `$${(v / 1000).toLocaleString()}k`;
                const statusPie = A.byStatus.filter(s => s.count > 0);
                const typePie = A.byType.filter(t => t.count > 0);
                return (
                <>
                  {/* ---------- OVERVIEW ---------- */}
                  {analyticsTab === 'overview' && (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(185px, 1fr))', gap: '1rem' }}>
                        <KPICard title="Total Entries" value={A.totals.total_entries} icon="📊" color="#6366f1" subtitle={`${A.totals.cancelled_count || 0} cancelled • ${A.totals.rejected_count || 0} rejected`} />
                        <KPICard title="Est. $" value={fmtMoney(A.totals.total_estimated_price)} icon="💵" color="#8b5cf6" subtitle={`${Number(A.totals.total_estimated_hours || 0).toLocaleString()} hrs`} />
                        <KPICard title="Submitted $" value={fmtMoney(A.totals.total_submitted)} icon="💰" color="#f59e0b" subtitle={`${Number(A.totals.total_submitted_hours || 0).toLocaleString()} hrs`} />
                        <KPICard title="Approved $" value={fmtMoney(A.totals.total_approved)} icon="✅" color="#10b981" subtitle={`${A.totals.approved_count} approved`} />
                        <KPICard title="Approval Rate" value={`${A.totals.approval_rate}%`} icon="🎯" color="#0ea5e9" subtitle="Approved ÷ Submitted $" />
                        <KPICard title="Est. Accuracy" value={`${A.totals.estimate_accuracy}%`} icon="📐" color="#14b8a6" subtitle="Submitted ÷ Estimated $" />
                        <KPICard title="Room & Board" value={fmtMoney(A.totals.total_room_and_board)} icon="🏨" color="#d97706" subtitle="all change orders" />
                        <KPICard title="Transport" value={fmtMoney(A.totals.total_transport)} icon="🚚" color="#0891b2" subtitle="all change orders" />
                        <KPICard title="Pending / Tracking" value={`${A.totals.pending || 0} / ${A.totals.tracking || 0}`} icon="🚦" color="#eab308" subtitle={`${A.totals.submitted || 0} submitted, ${A.totals.approved || 0} approved`} />
                      </div>
                      <div style={{ ...grid2, marginTop: '1rem' }}>
                        <div style={card}>
                          <h4 style={cardTitle}>Value Stream — Estimated → Submitted → Approved</h4>
                          <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={A.valueComparison} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                              <XAxis dataKey="name" tick={axisTick} />
                              <YAxis tickFormatter={moneyAxis} tick={axisTick} />
                              <Tooltip formatter={(v) => fmtMoney(v)} />
                              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                                {A.valueComparison.map((e, i) => <Cell key={i} fill={e.fill} />)}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                        <div style={card}>
                          <h4 style={cardTitle}>Status Distribution</h4>
                          <ResponsiveContainer width="100%" height={260}>
                            <PieChart>
                              <Pie data={statusPie} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => `${e.name}: ${e.count}`}>
                                {statusPie.map((e, i) => <Cell key={i} fill={e.color} />)}
                              </Pie>
                              <Tooltip />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </>
                  )}

                  {/* ---------- BY TYPE ---------- */}
                  {analyticsTab === 'type' && (
                    <div style={grid2}>
                      <div style={card}>
                        <h4 style={cardTitle}>Entries by Change Type</h4>
                        <ResponsiveContainer width="100%" height={260}>
                          <PieChart>
                            <Pie data={typePie} dataKey="count" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} label={(e) => `${e.count}`}>
                              {typePie.map((e, i) => <Cell key={i} fill={e.color} />)}
                            </Pie>
                            <Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div style={card}>
                        <h4 style={cardTitle}>$ by Change Type</h4>
                        <ResponsiveContainer width="100%" height={260}>
                          <BarChart data={A.byType} margin={{ top: 10, right: 10, left: 0, bottom: 30 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                            <XAxis dataKey="name" tick={{ ...axisTick, fontSize: 10 }} interval={0} angle={-12} textAnchor="end" height={50} />
                            <YAxis tickFormatter={moneyAxis} tick={axisTick} />
                            <Tooltip formatter={(v) => fmtMoney(v)} /><Legend wrapperStyle={{ fontSize: 12 }} />
                            <Bar dataKey="estimated_price" name="Estimated" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="submitted_price" name="Submitted" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="approved_price" name="Approved" fill="#10b981" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <div style={{ ...card, gridColumn: '1 / -1' }}>
                        <h4 style={cardTitle}>Change Type Breakdown</h4>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                          <thead>
                            <tr style={{ backgroundColor: '#f1f5f9' }}>
                              {['Change Type', 'Entries', 'Estimated $', 'Submitted $', 'Approved $'].map(h => (
                                <th key={h} style={{ padding: '0.5rem 0.75rem', textAlign: h === 'Change Type' ? 'left' : 'right', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#475569' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {A.byType.map((t, i) => (
                              <tr key={t.type} style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: i % 2 === 0 ? 'white' : '#f9fafb' }}>
                                <td style={{ padding: '0.5rem 0.75rem', fontWeight: '600', color: t.color }}>{t.name}</td>
                                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{t.count}</td>
                                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{fmtMoney(t.estimated_price)}</td>
                                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{fmtMoney(t.submitted_price)}</td>
                                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{fmtMoney(t.approved_price)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* ---------- BY DISCIPLINE ---------- */}
                  {analyticsTab === 'discipline' && (
                    <>
                      <div style={grid2}>
                        <div style={card}>
                          <h4 style={cardTitle}>Entries by Discipline</h4>
                          <ResponsiveContainer width="100%" height={Math.max(260, A.disciplines.length * 34)}>
                            <BarChart data={A.disciplines} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                              <XAxis type="number" allowDecimals={false} tick={axisTick} />
                              <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fill: '#475569' }} />
                              <Tooltip />
                              <Bar dataKey="entry_count" name="Entries" radius={[0, 4, 4, 0]}>
                                {A.disciplines.map((d, i) => <Cell key={i} fill={disciplineColorMap[d.discipline] || DISCIPLINE_PALETTE[i % DISCIPLINE_PALETTE.length]} />)}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                        <div style={card}>
                          <h4 style={cardTitle}>$ by Discipline</h4>
                          <ResponsiveContainer width="100%" height={Math.max(260, A.disciplines.length * 34)}>
                            <BarChart data={A.disciplines} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                              <XAxis type="number" tickFormatter={moneyAxis} tick={axisTick} />
                              <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fill: '#475569' }} />
                              <Tooltip formatter={(v) => fmtMoney(v)} /><Legend wrapperStyle={{ fontSize: 12 }} />
                              <Bar dataKey="submitted_price" name="Submitted" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                              <Bar dataKey="approved_price" name="Approved" fill="#10b981" radius={[0, 4, 4, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                      <div style={{ ...card, marginTop: '1rem', overflowX: 'auto' }}>
                        <h4 style={cardTitle}>Summary by Discipline</h4>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                          <thead>
                            <tr style={{ backgroundColor: '#f1f5f9' }}>
                              {['Discipline', 'Entries', 'Est. Hours', 'Est. $', 'Submitted $', 'Approved $', 'R&B $', 'Transport $', 'LS', 'T&M', 'Track', 'MatSup', 'Cancelled'].map(h => (
                                <th key={h} style={{ padding: '0.5rem 0.75rem', textAlign: h === 'Discipline' ? 'left' : 'right', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap', fontWeight: '600', color: '#475569' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {A.disciplines.map((d, i) => (
                              <tr key={d.discipline} style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: i % 2 === 0 ? 'white' : '#f9fafb' }}>
                                <td style={{ padding: '0.5rem 0.75rem', fontWeight: '600', color: '#1e293b' }}>{d.discipline}</td>
                                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{d.entry_count}</td>
                                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{Number(d.estimated_hours || 0).toLocaleString()}</td>
                                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{fmtMoney(d.estimated_price)}</td>
                                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{fmtMoney(d.submitted_price)}</td>
                                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{fmtMoney(d.approved_price)}</td>
                                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{fmtMoney(d.room_and_board)}</td>
                                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{fmtMoney(d.transport)}</td>
                                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{d.lump_sum_count || 0}</td>
                                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{d.tm_count || 0}</td>
                                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{d.tracking_only_count || 0}</td>
                                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{d.material_supply_count || 0}</td>
                                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{d.cancelled_count || 0}</td>
                              </tr>
                            ))}
                            <tr style={{ backgroundColor: '#f1f5f9', fontWeight: '700' }}>
                              <td style={{ padding: '0.5rem 0.75rem' }}>TOTAL</td>
                              <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{A.disciplines.reduce((s, d) => s + Number(d.entry_count || 0), 0)}</td>
                              <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{A.disciplines.reduce((s, d) => s + Number(d.estimated_hours || 0), 0).toLocaleString()}</td>
                              <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{fmtMoney(A.disciplines.reduce((s, d) => s + Number(d.estimated_price || 0), 0))}</td>
                              <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{fmtMoney(A.disciplines.reduce((s, d) => s + Number(d.submitted_price || 0), 0))}</td>
                              <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{fmtMoney(A.disciplines.reduce((s, d) => s + Number(d.approved_price || 0), 0))}</td>
                              <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{fmtMoney(A.disciplines.reduce((s, d) => s + Number(d.room_and_board || 0), 0))}</td>
                              <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{fmtMoney(A.disciplines.reduce((s, d) => s + Number(d.transport || 0), 0))}</td>
                              <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{A.disciplines.reduce((s, d) => s + Number(d.lump_sum_count || 0), 0)}</td>
                              <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{A.disciplines.reduce((s, d) => s + Number(d.tm_count || 0), 0)}</td>
                              <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{A.disciplines.reduce((s, d) => s + Number(d.tracking_only_count || 0), 0)}</td>
                              <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{A.disciplines.reduce((s, d) => s + Number(d.material_supply_count || 0), 0)}</td>
                              <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{A.disciplines.reduce((s, d) => s + Number(d.cancelled_count || 0), 0)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}

                  {/* ---------- BY STATUS ---------- */}
                  {analyticsTab === 'status' && (
                    <div style={grid2}>
                      <div style={card}>
                        <h4 style={cardTitle}>Entries by Status</h4>
                        <ResponsiveContainer width="100%" height={260}>
                          <PieChart>
                            <Pie data={statusPie} dataKey="count" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} label={(e) => `${e.count}`}>
                              {statusPie.map((e, i) => <Cell key={i} fill={e.color} />)}
                            </Pie>
                            <Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div style={card}>
                        <h4 style={cardTitle}>$ by Status</h4>
                        <ResponsiveContainer width="100%" height={260}>
                          <BarChart data={A.byStatus} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                            <XAxis dataKey="name" tick={{ ...axisTick, fontSize: 10 }} />
                            <YAxis tickFormatter={moneyAxis} tick={axisTick} />
                            <Tooltip formatter={(v) => fmtMoney(v)} /><Legend wrapperStyle={{ fontSize: 12 }} />
                            <Bar dataKey="submitted_price" name="Submitted" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="approved_price" name="Approved" fill="#10b981" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <div style={{ ...card, gridColumn: '1 / -1' }}>
                        <h4 style={cardTitle}>Approval Funnel</h4>
                        {(() => {
                          const stages = [
                            { key: 'pending', label: 'Pending' },
                            { key: 'submitted', label: 'Submitted' },
                            { key: 'approved', label: 'Approved' },
                          ];
                          const maxV = Math.max(1, ...stages.map(s => A.totals[s.key] || 0));
                          return stages.map(s => {
                            const v = A.totals[s.key] || 0;
                            return (
                              <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                                <span style={{ width: '90px', fontSize: '0.8rem', fontWeight: '600', color: STATUS_META[s.key].dot }}>{s.label}</span>
                                <div style={{ flex: 1, backgroundColor: '#f1f5f9', borderRadius: '6px', overflow: 'hidden', height: '26px' }}>
                                  <div style={{ width: `${(v / maxV) * 100}%`, backgroundColor: STATUS_META[s.key].dot, height: '100%', minWidth: v > 0 ? '2px' : 0, transition: 'width 0.3s', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: '6px', color: '#fff', fontSize: '0.78rem', fontWeight: '700' }}>{v || ''}</div>
                                </div>
                                <span style={{ width: '40px', fontSize: '0.8rem', textAlign: 'right', color: '#475569' }}>{v}</span>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  )}

                  {/* ---------- TRENDS ---------- */}
                  {analyticsTab === 'trends' && (
                    A.monthly.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                        <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>📅</div>
                        <div>No dated entries to plot. Add commencement dates to see trends.</div>
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gap: '1rem' }}>
                        <div style={card}>
                          <h4 style={cardTitle}>Cumulative $ Over Time</h4>
                          <ResponsiveContainer width="100%" height={280}>
                            <AreaChart data={A.monthly} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                              <defs>
                                <linearGradient id="ccSub" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#f59e0b" stopOpacity={0.6} /><stop offset="95%" stopColor="#f59e0b" stopOpacity={0.05} /></linearGradient>
                                <linearGradient id="ccApp" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.6} /><stop offset="95%" stopColor="#10b981" stopOpacity={0.05} /></linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                              <XAxis dataKey="month" tick={axisTick} />
                              <YAxis tickFormatter={moneyAxis} tick={axisTick} />
                              <Tooltip formatter={(v) => fmtMoney(v)} /><Legend wrapperStyle={{ fontSize: 12 }} />
                              <Area type="monotone" dataKey="cumulative_submitted" name="Cumulative Submitted" stroke="#f59e0b" fill="url(#ccSub)" />
                              <Area type="monotone" dataKey="cumulative_approved" name="Cumulative Approved" stroke="#10b981" fill="url(#ccApp)" />
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                        <div style={card}>
                          <h4 style={cardTitle}>New Entries & Submitted $ by Month</h4>
                          <ResponsiveContainer width="100%" height={280}>
                            <ComposedChart data={A.monthly} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                              <XAxis dataKey="month" tick={axisTick} />
                              <YAxis yAxisId="left" allowDecimals={false} tick={axisTick} />
                              <YAxis yAxisId="right" orientation="right" tickFormatter={moneyAxis} tick={axisTick} />
                              <Tooltip formatter={(v, n) => n === 'New Entries' ? v : fmtMoney(v)} /><Legend wrapperStyle={{ fontSize: 12 }} />
                              <Bar yAxisId="left" dataKey="count" name="New Entries" fill="#6366f1" radius={[4, 4, 0, 0]} barSize={26} />
                              <Line yAxisId="right" type="monotone" dataKey="submitted_price" name="Submitted $" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )
                  )}
                </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
      
      {/* DATA TABLE */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div 
          className="control-chart-table-scroll"
          style={{ 
            overflowX: 'auto',
            overflowY: 'auto',
            maxHeight: '500px'
          }}
        >
          <style>{`
            .control-chart-table-scroll::-webkit-scrollbar {
              width: 10px;
              height: 10px;
            }
            .control-chart-table-scroll::-webkit-scrollbar-track {
              background: #f1f1f1;
              border-radius: 5px;
            }
            .control-chart-table-scroll::-webkit-scrollbar-thumb {
              background: #c1c1c1;
              border-radius: 5px;
            }
            .control-chart-table-scroll::-webkit-scrollbar-thumb:hover {
              background: #a1a1a1;
            }
            .control-chart-table-scroll {
              scrollbar-width: thin;
              scrollbar-color: #c1c1c1 #f1f1f1;
            }
          `}</style>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ 
                  padding: '1rem 0.75rem', 
                  textAlign: 'center',
                  width: CHECKBOX_COL_WIDTH,
                  minWidth: CHECKBOX_COL_WIDTH,
                  borderBottom: '2px solid #dee2e6',
                  boxSizing: 'border-box',
                  ...getFreezeStyle('__checkbox', { isHeader: true })
                }}>
                  <input
                    type="checkbox"
                    checked={selectedRows.size === filteredData.length && filteredData.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
                
                {tableColumns.map(col => (
                  <th
                    key={col.key}
                    style={{
                      padding: '1rem 0.75rem',
                      textAlign: 'left',
                      fontWeight: '600',
                      color: '#374151',
                      borderBottom: '2px solid #dee2e6',
                      width: columnWidths[col.key],
                      minWidth: columnWidths[col.key],
                      userSelect: 'none',
                      whiteSpace: 'nowrap',
                      boxSizing: 'border-box',
                      ...getFreezeStyle(col.key, { isHeader: true })
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span
                        onClick={() => handleSort(col.key)}
                        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                      >
                        {col.label}
                        {sortConfig.key === col.key && (
                          <span style={{ fontSize: '0.75rem' }}>
                            {sortConfig.direction === 'asc' ? '▲' : '▼'}
                          </span>
                        )}
                      </span>
                      
                      <span
                        onClick={(e) => openFilterAtPosition(e, col.key)}
                        style={{ cursor: 'pointer', opacity: (columnFilters[col.key]?.length > 0 || filterMode[col.key]) ? 1 : 0.5 }}
                      >
                        <FilterIcon isActive={!!(columnFilters[col.key]?.length > 0 || filterMode[col.key])} />
                      </span>
                    </div>
                    
                    <div
                      onMouseDown={(e) => handleResizeStart(e, col.key)}
                      style={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: '5px',
                        cursor: 'col-resize',
                        backgroundColor: 'transparent'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dc3545'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    />
                  </th>
                ))}
                
              </tr>
            </thead>
            
            <tbody>
              {filteredData.length === 0 ? (
                <tr>
                  <td 
                    colSpan={visibleColumns.size + 1}
                    style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}
                  >
                    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📭</div>
                    <div>No entries found</div>
                    <div style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>Use the "Add New" button above to create an entry.</div>
                  </td>
                </tr>
              ) : (
                filteredData.map((row, idx) => {
                  const statusKey = getEntryStatus(row);
                  const statusMeta = STATUS_META[statusKey] || STATUS_META.pending;
                  const defaultBg = statusMeta.row;

                  return (
                  <tr
                    key={row.id}
                    style={{
                      backgroundColor: selectedRows.has(row.id) ? '#e0f2fe' : defaultBg,
                      transition: 'background-color 0.15s',
                      cursor: 'pointer',
                      color: statusMeta.strike ? statusMeta.rowText : undefined,
                      textDecoration: statusMeta.strike ? 'line-through' : 'none',
                      textDecorationColor: statusMeta.strike ? '#dc2626' : undefined,
                      borderLeft: `3px solid ${statusMeta.dot}`,
                    }}
                    onMouseEnter={(e) => {
                      if (selectedRows.has(row.id)) return;
                      e.currentTarget.style.backgroundColor = '#f3f4f6';
                      e.currentTarget.querySelectorAll('[data-frozen="1"]').forEach((td) => {
                        if (!td.dataset.keepBg) td.style.backgroundColor = '#f3f4f6';
                      });
                    }}
                    onMouseLeave={(e) => {
                      if (selectedRows.has(row.id)) return;
                      e.currentTarget.style.backgroundColor = defaultBg;
                      e.currentTarget.querySelectorAll('[data-frozen="1"]').forEach((td) => {
                        if (!td.dataset.keepBg) td.style.backgroundColor = defaultBg;
                      });
                    }}
                    onDoubleClick={() => { setEditingEntry(row); setShowEditModal(true); }}
                  >
                    <td
                      data-frozen="1"
                      style={{ 
                      padding: '0.75rem', 
                      textAlign: 'center', 
                      borderBottom: '1px solid #e5e7eb',
                      boxSizing: 'border-box',
                      ...getFreezeStyle('__checkbox', {
                        rowBg: selectedRows.has(row.id) ? '#e0f2fe' : defaultBg
                      })
                    }}>
                      <input
                        type="checkbox"
                        checked={selectedRows.has(row.id)}
                        onChange={() => handleSelectRow(row.id)}
                      />
                    </td>
                    
                    {tableColumns.map(col => {
                      const isInline = INLINE_EDITABLE_KEYS.has(col.key);
                      const isEditing = editingCell?.rowId === row.id && editingCell?.field === col.key;
                      const rowBg = selectedRows.has(row.id) ? '#e0f2fe' : defaultBg;
                      const maestroBg = col.key === 'maestro_status' && row[col.key] === 'approved'
                        ? '#d1fae5'
                        : (col.key === 'maestro_status' && row[col.key] === 'open' ? '#fef3c7' : null);
                      return (
                      <td
                        key={col.key}
                        data-frozen={frozenKeys.has(col.key) ? '1' : undefined}
                        data-keep-bg={maestroBg ? '1' : undefined}
                        style={{
                          padding: isEditing ? '4px' : '0.75rem',
                          borderBottom: '1px solid #e5e7eb',
                          color: '#374151',
                          maxWidth: columnWidths[col.key],
                          minWidth: columnWidths[col.key],
                          overflow: isEditing ? 'visible' : 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: isInline ? 'text' : undefined,
                          boxSizing: 'border-box',
                          ...getFreezeStyle(col.key, { rowBg: maestroBg || rowBg }),
                          ...(maestroBg ? { backgroundColor: maestroBg } : {}),
                        }}
                        title={isInline ? 'Click to edit this cell — double-click the row for the full form' : String(row[col.key] || '')}
                        onClick={(e) => {
                          if (!isInline || isEditing) return;
                          e.stopPropagation();
                          skipBlurSaveRef.current = false;
                          setEditingCell({ rowId: row.id, field: col.key });
                          setEditValue(initialInlineEditValue(row[col.key], col));
                        }}
                        onDoubleClick={(e) => {
                          if (isInline) e.stopPropagation();
                        }}
                      >
                        {col.key === 'maestro_status' ? (
                          <select
                            value={row[col.key] || ''}
                            onChange={(e) => handleInlineMaestroUpdate(row.id, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              fontWeight: '600',
                              cursor: 'pointer',
                              color: row[col.key] === 'approved' ? '#059669' : row[col.key] === 'open' ? '#d97706' : '#6b7280',
                              fontSize: '0.85rem',
                              padding: '2px 4px',
                            }}
                          >
                            <option value="">-</option>
                            <option value="open">Open</option>
                            <option value="approved">Approved</option>
                          </select>
                        ) : col.key === 'status' ? (
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            backgroundColor: statusMeta.badgeBg,
                            color: statusMeta.badgeText,
                            textDecoration: statusMeta.strike ? 'line-through' : 'none',
                            textDecorationColor: '#dc2626',
                            whiteSpace: 'nowrap',
                          }}>
                            {statusMeta.label}
                          </span>
                        ) : col.key === 'billing_type' && row[col.key] ? (
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            backgroundColor: CHANGE_TYPE_COLORS[row[col.key]]?.border || '#e5e7eb',
                            color: CHANGE_TYPE_COLORS[row[col.key]]?.text || '#374151',
                          }}>
                            {row[col.key]}
                          </span>
                        ) : isEditing ? (
                          <InlineCellEditor
                            col={col}
                            value={editValue}
                            onChange={setEditValue}
                            onSave={(val) => {
                              if (skipBlurSaveRef.current) {
                                skipBlurSaveRef.current = false;
                                return;
                              }
                              handleInlineCellSave(row, col, val);
                            }}
                            onCancel={() => {
                              skipBlurSaveRef.current = true;
                              setEditingCell(null);
                            }}
                          />
                        ) : formatCellValue(row[col.key], col.type)}
                      </td>
                      );
                    })}
                    
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        
        {/* Table Footer */}
        <div style={{
          padding: '1rem',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#f8f9fa'
        }}>
          <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>
            Showing {filteredData.length} of {data.length} entries
            {selectedRows.size > 0 && ` • ${selectedRows.size} selected`}
          </span>
          
          {selectedRows.size > 0 && (
            <Button onClick={handleBulkDelete} variant="danger" icon="🗑️">
              Delete Selected
            </Button>
          )}
        </div>
      </div>
      
      {/* MODALS */}
      
      {/* Add Modal */}
      <Modal 
        isOpen={showAddModal} 
        onClose={() => setShowAddModal(false)} 
        title="Add New Entry"
        width="700px"
      >
        <EntryForm 
          onSubmit={handleAdd} 
          onCancel={() => setShowAddModal(false)}
          jobNumber={jobNumber}
          jobName={jobName}
          projectId={projectId}
          currentUserName={currentUserName}
          instance={instance}
          accounts={accounts}
          onPrintPDF={handlePrintPDF}
          config={config}
        />
      </Modal>
      
      {/* Edit Modal */}
      <Modal 
        isOpen={showEditModal} 
        onClose={() => { setShowEditModal(false); setEditingEntry(null); }} 
        title="Edit Entry"
        width="700px"
      >
        {editingEntry && (
          <EntryForm 
            initialData={editingEntry}
            onSubmit={handleEdit} 
            onCancel={() => { setShowEditModal(false); setEditingEntry(null); }}
            jobNumber={jobNumber}
            jobName={jobName}
            projectId={projectId}
            currentUserName={currentUserName}
            instance={instance}
            accounts={accounts}
            onPrintPDF={handlePrintPDF}
            config={config}
          />
        )}
      </Modal>
      
      {/* Configuration Modal */}
      <Modal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        title="Change Log Configuration"
        width="960px"
      >
        <ConfigForm
          initialConfig={config}
          onSave={handleSaveConfig}
          onCancel={() => setShowConfigModal(false)}
        />
      </Modal>

      {/* Help Modal */}
      <Modal 
        isOpen={showHelpModal} 
        onClose={() => setShowHelpModal(false)} 
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            Change Log — Help Guide
            <ListenButton compact guideId="change-log-help" title="Change Log Help" />
          </span>
        }
        width="850px"
      >
        <HelpWithVideoTour guideId="change-log-help" title="Change Log Help" accent="#dc2626">
          <HelpContent />
        </HelpWithVideoTour>
      </Modal>
      
      {/* Filter Dropdown */}
      {openFilterColumn && (
        <FilterTemplateDropdown
          columnKey={openFilterColumn}
          columnLabel={columnLabels[openFilterColumn] || openFilterColumn}
          isNumericColumn={numericColumnKeys.includes(openFilterColumn)}
          isDateColumn={dateColumnKeys.includes(openFilterColumn)}
          columnFilters={columnFilters}
          filterMode={filterMode}
          filterText={filterText}
          filterSearch={filterSearch}
          filterButtonPositions={filterButtonPositions}
          setOpenFilterColumn={setOpenFilterColumn}
          setFilterSearch={setFilterSearch}
          getUniqueValues={getUniqueValues}
          handleFilterToggle={handleFilterToggle}
          selectAllColumnFilter={selectAllColumnFilter}
          clearColumnFilter={clearColumnFilter}
          applyTextFilter={applyTextFilter}
        />
      )}
      
      {/* Toast Notification */}
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}
      
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

// ============================================
// DYNAMIC LIST ITEM COMPONENT
// ============================================
const DynamicListSection = memo(({ title, items, fields, onChange, addLabel }) => {
  const inputStyle = {
    width: '100%', padding: '0.5rem', border: '1px solid #d1d5db',
    borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box'
  };

  const handleAdd = () => {
    const emptyRow = {};
    fields.forEach(f => { emptyRow[f.key] = ''; });
    onChange([...items, emptyRow]);
  };

  const handleRemove = (idx) => {
    onChange(items.filter((_, i) => i !== idx));
  };

  const handleItemChange = (idx, key, value) => {
    const updated = items.map((item, i) => i === idx ? { ...item, [key]: value } : item);
    onChange(updated);
  };

  return (
    <div style={{ marginTop: '1rem', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', backgroundColor: '#fafafa' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <span style={{ fontWeight: '600', fontSize: '0.9rem', color: '#374151' }}>{title}</span>
        <button
          type="button"
          onClick={handleAdd}
          style={{
            padding: '0.3rem 0.75rem', backgroundColor: '#28a745', color: 'white',
            border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '500'
          }}
        >
          + {addLabel || 'Add'}
        </button>
      </div>
      {items.length === 0 && (
        <div style={{ color: '#9ca3af', fontSize: '0.85rem', padding: '0.5rem 0', fontStyle: 'italic' }}>
          No items. Click "+" to add.
        </div>
      )}
      {items.map((item, idx) => (
        <div key={idx} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.4rem' }}>
          {fields.map(f => (
            <div key={f.key} style={{ flex: f.flex || 1 }}>
              {idx === 0 && (
                <div style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '2px' }}>{f.label}</div>
              )}
              <input
                type={f.type || 'text'}
                step={f.type === 'number' ? '0.01' : undefined}
                value={item[f.key] || ''}
                onChange={(e) => handleItemChange(idx, f.key, e.target.value)}
                placeholder={f.placeholder || f.label}
                style={inputStyle}
              />
            </div>
          ))}
          <button
            type="button"
            onClick={() => handleRemove(idx)}
            style={{
              padding: '0.3rem 0.5rem', backgroundColor: '#fef3f3', color: '#dc3545',
              border: '1px solid #fca5a5', borderRadius: '4px', cursor: 'pointer',
              fontSize: '0.8rem', marginTop: idx === 0 ? '16px' : '0'
            }}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
});

// ============================================
// ENTRY FORM COMPONENT (Redesigned with dynamic sections)
// ============================================
const TMLabourSection = memo(({ trades, onChange, markupPct, config }) => {
  const inputStyle = {
    width: '100%', padding: '0.4rem', border: '1px solid #d1d5db',
    borderRadius: '4px', fontSize: '0.8rem', boxSizing: 'border-box', textAlign: 'right'
  };

  const cfgTrades = Array.isArray(config?.trades) ? config.trades : [];
  const [openTradeIdx, setOpenTradeIdx] = useState(null);

  // Snapshot the calendar + per-diems we need to compute hours-split and R&B/Transport.
  const buildSnapshot = (src) => ({
    straight_hours_per_week: parseFloat(src.straight_hours_per_week) || 0,
    overtime_hours_per_week: parseFloat(src.overtime_hours_per_week) || 0,
    double_time_hours_per_week: parseFloat(src.double_time_hours_per_week) || 0,
    days_per_week: parseFloat(src.days_per_week) || 0,
    room_board_per_diem: parseFloat(src.room_board_per_diem) || 0,
    transport_per_diem: parseFloat(src.transport_per_diem) || 0,
  });

  const handleAddTrade = () => {
    onChange([...trades, {
      trade_name: '', total_hours: '', cfg: null,
      st_hours: '', st_rate: '',
      ot_hours: '', ot_rate: '',
      dt_hours: '', dt_rate: '',
    }]);
  };

  const handleRemoveTrade = (idx) => {
    onChange(trades.filter((_, i) => i !== idx));
  };

  const setTradeFields = (idx, fields) => {
    onChange(trades.map((t, i) => i === idx ? { ...t, ...fields } : t));
  };

  const handleTradeChange = (idx, field, value) => {
    setTradeFields(idx, { [field]: value });
  };

  // Selecting / typing a trade name. If it matches a configured trade, snapshot its
  // calendar + per-diems, auto-fill ST/OT/DT rates, and re-split any existing total.
  const handleTradeNameChange = (idx, value) => {
    const t = trades[idx];
    const match = cfgTrades.find(tr => (tr.name || '').trim().toLowerCase() === value.trim().toLowerCase() && value.trim() !== '');
    const fields = { trade_name: value };
    const calcCfg = match ? buildSnapshot(match) : (config || DEFAULT_CONFIG);
    if (match) {
      fields.cfg = buildSnapshot(match);
      fields.st_rate = match.straight_rate ?? '';
      fields.ot_rate = match.overtime_rate ?? '';
      fields.dt_rate = match.double_time_rate ?? '';
    } else {
      fields.cfg = null;
    }
    if (t.total_hours !== undefined && t.total_hours !== '') {
      const split = distributeHoursByCalendar(t.total_hours, calcCfg);
      fields.st_hours = split.st || ''; fields.ot_hours = split.ot || ''; fields.dt_hours = split.dt || '';
    }
    setTradeFields(idx, fields);
  };

  // Enter a single total -> auto-split into ST/OT/DT using the trade's calendar.
  const handleTotalHoursChange = (idx, value) => {
    const t = trades[idx];
    const cfg = resolveTradeConfig(t, config);
    const split = distributeHoursByCalendar(value, cfg);
    setTradeFields(idx, {
      total_hours: value,
      st_hours: split.st || '', ot_hours: split.ot || '', dt_hours: split.dt || '',
    });
  };

  // Editing a detail row (ST/OT/DT) -> the trade total reflects the sum.
  const handleDetailHoursChange = (idx, hKey, value) => {
    const t = { ...trades[idx], [hKey]: value };
    const sum = (parseFloat(t.st_hours) || 0) + (parseFloat(t.ot_hours) || 0) + (parseFloat(t.dt_hours) || 0);
    setTradeFields(idx, { [hKey]: value, total_hours: sum });
  };

  const calcRow = (hours, rate) => (parseFloat(hours) || 0) * (parseFloat(rate) || 0);
  const calcTradeTotal = (t) => calcRow(t.st_hours, t.st_rate) + calcRow(t.ot_hours, t.ot_rate) + calcRow(t.dt_hours, t.dt_rate);
  const calcTradeHours = (t) => (parseFloat(t.st_hours) || 0) + (parseFloat(t.ot_hours) || 0) + (parseFloat(t.dt_hours) || 0);
  const grandTotal = trades.reduce((s, t) => s + calcTradeTotal(t), 0);

  const thStyle = { padding: '0.4rem', fontSize: '0.75rem', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb', textAlign: 'right' };
  const tdStyle = { padding: '0.2rem 0.3rem', fontSize: '0.8rem' };

  return (
    <div style={{ marginTop: '0.5rem', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', backgroundColor: '#fafafa' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <span style={{ fontWeight: '600', fontSize: '0.9rem', color: '#374151' }}>Labour (by Trade)</span>
        <button type="button" onClick={handleAddTrade} style={{
          padding: '0.3rem 0.75rem', backgroundColor: '#28a745', color: 'white',
          border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '500'
        }}>+ Add Trade</button>
      </div>
      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.4rem' }}>
        Pick a <strong>Trade</strong> from the dropdown (from Configuration) to auto-fill its rates &amp; per-diems, or type a custom one.
        Enter total <strong>Hours</strong> to auto-split into ST/OT/DT by that trade's calendar, or fill ST/OT/DT directly and the total sums up.
      </div>
      {trades.length === 0 ? (
        <div style={{ color: '#9ca3af', fontSize: '0.85rem', padding: '0.5rem 0', fontStyle: 'italic' }}>
          No trades. Click "+ Add Trade" to add a trade category.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, textAlign: 'left', width: '30%' }}>Trade</th>
              <th style={{ ...thStyle, width: '16%' }}>Hours</th>
              <th style={{ ...thStyle, width: '20%' }}>Hourly Rate</th>
              <th style={{ ...thStyle, width: '28%' }}>Total</th>
              <th style={{ ...thStyle, width: '6%' }}></th>
            </tr>
          </thead>
          <tbody>
            {trades.map((t, idx) => {
              const stTotal = calcRow(t.st_hours, t.st_rate);
              const otTotal = calcRow(t.ot_hours, t.ot_rate);
              const dtTotal = calcRow(t.dt_hours, t.dt_rate);
              const totalHoursVal = (t.total_hours !== undefined && t.total_hours !== '') ? t.total_hours : (calcTradeHours(t) || '');
              return (
                <React.Fragment key={idx}>
                  <tr style={{ backgroundColor: '#f0f4f8' }}>
                    <td style={tdStyle}>
                      <div style={{ position: 'relative' }}>
                        <input type="text" value={t.trade_name || ''} onChange={(e) => handleTradeNameChange(idx, e.target.value)}
                          onFocus={() => cfgTrades.length > 0 && setOpenTradeIdx(idx)}
                          onBlur={() => setTimeout(() => setOpenTradeIdx(prev => (prev === idx ? null : prev)), 150)}
                          placeholder={cfgTrades.length > 0 ? 'Select or type a trade' : 'Type a trade name'}
                          style={{ ...inputStyle, textAlign: 'left', fontWeight: '600', paddingRight: cfgTrades.length > 0 ? '1.4rem' : '0.4rem' }} />
                        {cfgTrades.length > 0 && (
                          <span onMouseDown={(e) => { e.preventDefault(); setOpenTradeIdx(prev => (prev === idx ? null : idx)); }}
                            style={{ position: 'absolute', right: '0.45rem', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#6b7280', fontSize: '0.65rem', userSelect: 'none' }}>▼</span>
                        )}
                        {openTradeIdx === idx && cfgTrades.length > 0 && (() => {
                          const q = (t.trade_name || '').trim().toLowerCase();
                          const filtered = cfgTrades.filter(tr => !q || (tr.name || '').toLowerCase().includes(q));
                          const list = filtered.length > 0 ? filtered : cfgTrades;
                          return (
                            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30, backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: '180px', overflowY: 'auto', marginTop: '2px' }}>
                              {list.map((tr, i) => (
                                <div key={i} onMouseDown={(e) => { e.preventDefault(); handleTradeNameChange(idx, tr.name); setOpenTradeIdx(null); }}
                                  style={{ padding: '0.4rem 0.6rem', cursor: 'pointer', fontSize: '0.8rem', borderBottom: i < list.length - 1 ? '1px solid #f1f5f9' : 'none', textAlign: 'left' }}
                                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#eff6ff'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}>
                                  {tr.name || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>(unnamed)</span>}
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <input type="number" step="0.5" value={totalHoursVal} onChange={(e) => handleTotalHoursChange(idx, e.target.value)}
                        placeholder="total" style={{ ...inputStyle, fontWeight: '600' }} title="Total hours for this trade (auto-splits into ST/OT/DT)" />
                    </td>
                    <td style={tdStyle}></td>
                    <td style={{ ...tdStyle, fontWeight: '600', textAlign: 'right', fontSize: '0.85rem' }}>
                      ${fmtCurrency(calcTradeTotal(t))}
                    </td>
                    <td style={tdStyle}>
                      <button type="button" onClick={() => handleRemoveTrade(idx)} style={{
                        padding: '0.2rem 0.4rem', backgroundColor: '#fef3f3', color: '#dc3545',
                        border: '1px solid #fca5a5', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem'
                      }}>×</button>
                    </td>
                  </tr>
                  {[
                    { label: 'Straight Time', hKey: 'st_hours', rKey: 'st_rate', total: stTotal },
                    { label: 'Overtime', hKey: 'ot_hours', rKey: 'ot_rate', total: otTotal },
                    { label: 'Double Time', hKey: 'dt_hours', rKey: 'dt_rate', total: dtTotal },
                  ].map(row => (
                    <tr key={row.label}>
                      <td style={{ ...tdStyle, paddingLeft: '1.5rem', fontSize: '0.8rem', color: '#6b7280' }}>{row.label}</td>
                      <td style={tdStyle}>
                        <input type="number" step="0.5" value={t[row.hKey] || ''} onChange={(e) => handleDetailHoursChange(idx, row.hKey, e.target.value)}
                          placeholder="0" style={inputStyle} />
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                          <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>$</span>
                          <input type="number" step="0.01" value={t[row.rKey] || ''} onChange={(e) => handleTradeChange(idx, row.rKey, e.target.value)}
                            placeholder="0.00" style={inputStyle} />
                        </div>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontSize: '0.8rem' }}>${fmtCurrency(row.total)}</td>
                      <td style={tdStyle}></td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
      <div style={{ textAlign: 'right', fontWeight: '700', fontSize: '0.9rem', padding: '0.5rem 0.3rem', color: '#1e293b', borderTop: '2px solid #e5e7eb', marginTop: '0.25rem' }}>
        Labour Total: ${fmtCurrency(grandTotal)}
      </div>
    </div>
  );
});

const MarkupListSection = memo(({ title, items, onChange, markupPct, addLabel }) => {
  const inputStyle = {
    width: '100%', padding: '0.4rem', border: '1px solid #d1d5db',
    borderRadius: '4px', fontSize: '0.8rem', boxSizing: 'border-box', textAlign: 'right'
  };
  const markup = parseFloat(markupPct) || 8.75;

  const handleAdd = () => {
    onChange([...items, { description: '', quantity: '', unit_price: '' }]);
  };
  const handleRemove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const handleChange = (idx, field, value) => {
    onChange(items.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };
  const calcTotal = (item) => {
    const base = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
    return base + (base * markup / 100);
  };
  const grandTotal = items.reduce((s, item) => s + calcTotal(item), 0);

  const thStyle = { padding: '0.4rem', fontSize: '0.75rem', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb', textAlign: 'right' };
  const tdStyle = { padding: '0.2rem 0.3rem' };

  return (
    <div style={{ marginTop: '0.5rem', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', backgroundColor: '#fafafa' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <span style={{ fontWeight: '600', fontSize: '0.9rem', color: '#374151' }}>{title}</span>
        <button type="button" onClick={handleAdd} style={{
          padding: '0.3rem 0.75rem', backgroundColor: '#28a745', color: 'white',
          border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '500'
        }}>+ {addLabel || 'Add Line'}</button>
      </div>
      {items.length === 0 ? (
        <div style={{ color: '#9ca3af', fontSize: '0.85rem', padding: '0.5rem 0', fontStyle: 'italic' }}>
          No items. Click "+" to add.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, textAlign: 'left', width: '35%' }}>Description</th>
              <th style={{ ...thStyle, width: '12%' }}>Qty/Hours</th>
              <th style={{ ...thStyle, width: '18%' }}>Unit Price / Hourly Rate</th>
              <th style={{ ...thStyle, width: '10%' }}>Markup</th>
              <th style={{ ...thStyle, width: '15%' }}>Total</th>
              <th style={{ ...thStyle, width: '5%' }}></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <tr key={idx}>
                <td style={tdStyle}>
                  <input type="text" value={item.description || ''} onChange={(e) => handleChange(idx, 'description', e.target.value)}
                    placeholder="Description" style={{ ...inputStyle, textAlign: 'left' }} />
                </td>
                <td style={tdStyle}>
                  <input type="number" step="0.5" value={item.quantity || ''} onChange={(e) => handleChange(idx, 'quantity', e.target.value)}
                    placeholder="0" style={inputStyle} />
                </td>
                <td style={tdStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                    <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>$</span>
                    <input type="number" step="0.01" value={item.unit_price || ''} onChange={(e) => handleChange(idx, 'unit_price', e.target.value)}
                      placeholder="0.00" style={inputStyle} />
                  </div>
                </td>
                <td style={{ ...tdStyle, textAlign: 'center', fontSize: '0.8rem', color: '#6b7280' }}>{markup}%</td>
                <td style={{ ...tdStyle, textAlign: 'right', fontSize: '0.85rem', fontWeight: '500' }}>${fmtCurrency(calcTotal(item))}</td>
                <td style={tdStyle}>
                  <button type="button" onClick={() => handleRemove(idx)} style={{
                    padding: '0.2rem 0.4rem', backgroundColor: '#fef3f3', color: '#dc3545',
                    border: '1px solid #fca5a5', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem'
                  }}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div style={{ textAlign: 'right', fontWeight: '700', fontSize: '0.9rem', padding: '0.5rem 0.3rem', color: '#1e293b', borderTop: '2px solid #e5e7eb', marginTop: '0.25rem' }}>
        {title} Total: ${fmtCurrency(grandTotal)}
      </div>
    </div>
  );
});

const HelpContent = memo(() => {
  const [activeSection, setActiveSection] = useState('overview');

  const sections = [
    { id: 'overview', label: 'Overview', icon: '📋' },
    { id: 'notifications', label: 'Accounting Emails', icon: '📬' },
    { id: 'changetypes', label: 'Change Types', icon: '🏷️' },
    { id: 'status', label: 'Status & Colors', icon: '🚦' },
    { id: 'toolbar', label: 'Toolbar', icon: '🔧' },
    { id: 'table', label: 'Table', icon: '📊' },
    { id: 'addentry', label: 'Add / Edit Entry', icon: '➕' },
    { id: 'lumpsum', label: 'Lump Sum', icon: '📦' },
    { id: 'tm', label: 'T&M', icon: '⏰' },
    { id: 'pdf', label: 'PDF Export', icon: '📄' },
    { id: 'shortcuts', label: 'Tips & Shortcuts', icon: '⚡' },
  ];

  const headingStyle = { margin: '0 0 0.75rem 0', fontSize: '1.1rem', color: '#1e293b', fontWeight: '700' };
  const subHeadStyle = { margin: '1rem 0 0.5rem 0', fontSize: '0.95rem', color: '#334155', fontWeight: '600' };
  const paraStyle = { margin: '0 0 0.75rem 0', color: '#475569', fontSize: '0.88rem', lineHeight: '1.6' };
  const bulletList = { margin: '0 0 0.75rem 0', paddingLeft: '1.25rem', color: '#475569', fontSize: '0.88rem', lineHeight: '1.8' };
  const kbdStyle = { display: 'inline-block', padding: '1px 6px', backgroundColor: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.8rem', fontFamily: 'monospace', color: '#334155' };
  const tipBox = (text) => (
    <div style={{ backgroundColor: '#f0fdf4', padding: '0.75rem 1rem', borderRadius: '6px', borderLeft: '3px solid #22c55e', marginBottom: '0.75rem' }}>
      <span style={{ fontWeight: '600', color: '#166534', marginRight: '0.5rem' }}>Tip:</span>
      <span style={{ color: '#14532d', fontSize: '0.85rem' }}>{text}</span>
    </div>
  );
  const noteBox = (text) => (
    <div style={{ backgroundColor: '#fef9c3', padding: '0.75rem 1rem', borderRadius: '6px', borderLeft: '3px solid #eab308', marginBottom: '0.75rem' }}>
      <span style={{ fontWeight: '600', color: '#854d0e', marginRight: '0.5rem' }}>Note:</span>
      <span style={{ color: '#713f12', fontSize: '0.85rem' }}>{text}</span>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div>
            <h3 style={headingStyle}>What is the Change Log?</h3>
            <p style={paraStyle}>
              The Change Log (Control Chart) is the central hub for tracking all change orders, extras, and related
              documentation on a project. It captures the full lifecycle of a change — from initial submission through
              approval, invoicing, and completion.
            </p>
            <h4 style={subHeadStyle}>Key Concepts</h4>
            <ul style={bulletList}>
              <li><strong>Change Order / Extra</strong> — A scope modification with associated cost and schedule impact.</li>
              <li><strong>Lump Sum</strong> — Fixed-price change with itemized Direct, Material, Subcontractors, Equipment, and Tax costs.</li>
              <li><strong>T&M (Time & Materials)</strong> — Cost-plus change with labour by trade (ST/OT/DT hours and rates), material, subcontractors, equipment with configurable markup, and optional Room & Board.</li>
              <li><strong>Tracking Only</strong> — An entry for record-keeping/tracking purposes only. No cost details required. Highlighted in purple.</li>
              <li><strong>Material Supply Only</strong> — A change covering only material supply with no labour. 0 labour hours is acceptable. Highlighted in green.</li>
              <li><strong>Status</strong> — The lifecycle stage of a change: <strong>Pending → Submitted → Approved</strong>, plus <strong>Tracking</strong> (driven by the change type), and the manual terminal states <strong>Cancelled</strong> and <strong>Rejected</strong>. The status is set automatically from the cost boxes (see the Status &amp; Colors section).</li>
              <li><strong>RFI / POCR / POCN</strong> — Reference document numbers linked to each change entry.</li>
              <li><strong>Estimated Hours / $</strong> — Manual entry (SWAG/draft estimate) provided when creating a change order. A value here marks the entry <strong>Pending</strong>.</li>
              <li><strong>Submitted Hours / $</strong> — Auto-calculated from the detail line items. A Submitted $ greater than zero marks the entry <strong>Submitted</strong>.</li>
              <li><strong>Approved Price</strong> — Entering an approved amount marks the entry <strong>Approved</strong>. The <strong>Rejected</strong> button beside it marks the entry <strong>Rejected</strong>.</li>
              <li><strong>Maestro</strong> — A separate column tracking whether an extra has been opened ("Open") or approved ("Approved") in the Maestro system. This is independent of the lifecycle Status.</li>
            </ul>
            <h4 style={subHeadStyle}>Workflow</h4>
            <ol style={bulletList}>
              <li>Select a Change Type (Lump Sum, T&M, Tracking Only, or Material Supply Only) and Discipline.</li>
              <li>Enter Estimated Hours and Estimated $ as a draft/SWAG estimate — the entry becomes <strong>Pending</strong> (not required for Tracking Only, which becomes <strong>Tracking</strong>; 0 hours OK for T&M and Material Supply Only).</li>
              <li>Fill in cost details, drawings, RFIs, schedule impact. As the Submitted $ is calculated, the status advances to <strong>Submitted</strong>.</li>
              <li>Submit the entry — the Extra # is auto-generated and Accounting is emailed immediately to open the extra.</li>
              <li>Track submitted vs. approved pricing in the table. Enter an Approved Price to move the status to <strong>Approved</strong>, or click <strong>Rejected</strong> if the client rejects it.</li>
              <li>Generate a PDF for client submission or record-keeping.</li>
              <li>Update with invoice info when settled. Use the Maestro column to mark Open/Approved in Maestro.</li>
              <li>If an extra is no longer needed, select it and click "Cancel/Restore" — it stays visible but is marked <strong>Cancelled</strong>.</li>
              <li>Use the <strong>View</strong> dropdown in the toolbar to focus the table on a single <strong>Discipline</strong>.</li>
            </ol>
            {noteBox('Accounting is emailed twice from this page: immediately when you add an entry, and every Monday with a full Change Log snapshot (pending / submitted / approved and Maestro). Opt out under Profile → Notification Settings.')}
            <h4 style={subHeadStyle}>Who can open this page</h4>
            <ul style={bulletList}>
              <li><strong>Construction</strong> users whose role includes Change Management.</li>
              <li><strong>Cost Clerks</strong> in Accounting, so they can set the Maestro column after opening the extra. Other Accounting roles only get the emails.</li>
              <li>Anyone who can open the page can add, edit, cancel, and delete entries. There is no view-only mode.</li>
            </ul>
          </div>
        );

      case 'notifications':
        return (
          <div>
            <h3 style={headingStyle}>Accounting emails</h3>
            <p style={paraStyle}>
              The live Change Log (this page) is the source of truth for Accounting notifications.
              The hidden Progress Tracking → Change Orders / CCR table is not used for the weekly digest.
            </p>
            <h4 style={subHeadStyle}>New Extra Request (on create)</h4>
            <p style={paraStyle}>
              Adding an entry emails every active Accounting user with job number, extra number, change type,
              discipline, description, estimated hours, and estimated dollars, plus a portal link. They open
              the extra in Maestro and set the Maestro column to <strong>Open</strong>.
            </p>
            <h4 style={subHeadStyle}>Monday Change Log report (8:00 AM ET)</h4>
            <p style={paraStyle}>
              Each Monday, Accounting gets one email per job that has Change Log rows. It is a
              <strong> full snapshot</strong> of that job&apos;s extras — not a “what changed since last week”
              delta — with lifecycle status (Pending / Submitted / Approved / Tracking / Cancelled / Rejected),
              submitted and approved dollars, and Maestro status.
            </p>
            <ul style={bulletList}>
              <li>Recipients: active users whose role template is in Accounting (department 2).</li>
              <li>Jobs with no Change Log rows are skipped.</li>
              <li>Feature keys: <kbd style={kbdStyle}>control_chart_new_entry</kbd> and <kbd style={kbdStyle}>control_chart_weekly_report</kbd> — listed under Admin → Notifications.</li>
              <li>Anyone can turn these off in Profile → Notification Settings.</li>
            </ul>
            {tipBox('If Accounting only wants extras that moved last week, that is a product choice still to confirm — the current report lists the whole job.')}
          </div>
        );

      case 'changetypes':
        return (
          <div>
            <h3 style={headingStyle}>Change Types</h3>
            <p style={paraStyle}>The Change Log supports four change types, each with different validation rules and visual styling.</p>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', marginBottom: '1rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#f1f5f9' }}>
                  <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', borderBottom: '2px solid #e2e8f0', width: '160px' }}>Type</th>
                  <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Description</th>
                  <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', borderBottom: '2px solid #e2e8f0', width: '120px' }}>Hours Required?</th>
                  <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', borderBottom: '2px solid #e2e8f0', width: '80px' }}>Color</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Lump Sum', 'Fixed-price change order. Built internally from estimated costs + markup; the client PDF shows selling totals only.', 'No (0 OK)', { bg: '#bae6fd', text: '#0369a1' }],
                  ['T&M', 'Time & Materials change order with labour by trade (ST/OT/DT), markup on material/subs/equipment, and optional Room & Board. Full detail appears on the client PDF.', 'No (0 OK)', { bg: '#fde68a', text: '#92400e' }],
                  ['Tracking Only', 'For record-keeping purposes only. Optional internal cost-builder; cost detail stays internal.', 'No', { bg: '#d8b4fe', text: '#7e22ce' }],
                  ['Material Supply Only', 'Change covering only material supply with no labour component. 0 labour hours is explicitly accepted.', 'No (0 OK)', { bg: '#a7f3d0', text: '#065f46' }],
                ].map(([type, desc, hours, color], i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '0.5rem 0.75rem', fontWeight: '600' }}>
                      <span style={{ padding: '2px 8px', borderRadius: '12px', backgroundColor: color.bg, color: color.text, fontSize: '0.8rem' }}>{type}</span>
                    </td>
                    <td style={{ padding: '0.5rem 0.75rem', color: '#475569' }}>{desc}</td>
                    <td style={{ padding: '0.5rem 0.75rem', color: '#475569' }}>{hours}</td>
                    <td style={{ padding: '0.5rem 0.75rem' }}>
                      <div style={{ width: '24px', height: '24px', borderRadius: '4px', backgroundColor: color.bg, border: `1px solid ${color.text}` }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h4 style={subHeadStyle}>Color Coding in the Table</h4>
            <p style={paraStyle}>
              Rows in the change log are color-coded by their <strong>lifecycle status</strong> (not the change type).
              See the <strong>Status &amp; Colors</strong> section for the full legend. The change type is still shown as a colored pill in the Change Type column.
            </p>

            <h4 style={subHeadStyle}>Internal Cost-Builder vs. Client PDF</h4>
            <p style={paraStyle}>
              All change types use the same internal cost-builder (labour by trade, markup on
              material/subs/equipment, Room & Board, Transport). For <strong>T&M</strong>, this detail is printed on the
              client PDF. For <strong>Lump Sum, Material Supply Only, and Tracking Only</strong>, the buildup is
              <strong> internal only</strong> — the client PDF shows the resulting selling totals without rates,
              unit prices, or markup.
            </p>

            <h4 style={subHeadStyle}>Configuration (per trade)</h4>
            <p style={paraStyle}>
              Use the <strong>Configuration</strong> button at the top of the Change Log to define the project&rsquo;s
              <strong> trades</strong>. Each trade carries its own work calendar (Straight/Overtime/Double Time hours per
              week and days per week), its own <strong>ST/OT/DT hourly rates</strong>, and its own Room &amp; Board and
              Transport per-diems. The <strong>Default values</strong> section seeds new trades and is the fallback for any
              custom trade typed directly into a change order.
            </p>
            <p style={paraStyle}>
              In the Add / Edit form, pick a trade from the <strong>Trade</strong> dropdown and its rates &amp; per-diems
              auto-fill (you can still override them, or type a custom trade name). Each trade&rsquo;s calendar drives the
              automatic ST/OT/DT split of its total hours (no crew &mdash; proration is by calendar only), and its per-diems
              convert to an hourly cost so Room &amp; Board and Transport are summed <strong>per trade</strong> on the change order.
            </p>
            {tipBox('Room & Board, Transport, and Self-Assessed Tax are all included in the Change Order Total and carry no markup.')}
          </div>
        );

      case 'status':
        return (
          <div>
            <h3 style={headingStyle}>Status &amp; Color Coding</h3>
            <p style={paraStyle}>
              Every change moves through a <strong>lifecycle status</strong>. The status is set <strong>automatically</strong> from the
              cost boxes in the Add / Edit form — you don't pick it from a dropdown. Cancelled and Rejected are the only manual states.
            </p>

            <h4 style={subHeadStyle}>How the status is determined</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', marginBottom: '1rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#f1f5f9' }}>
                  <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', borderBottom: '2px solid #e2e8f0', width: '120px' }}>Status</th>
                  <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Trigger</th>
                  <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', borderBottom: '2px solid #e2e8f0', width: '90px' }}>Color</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['pending', 'A value greater than zero in the Estimated $ box.'],
                  ['submitted', 'A value greater than zero in the Submitted $ box (auto-calculated from the cost line items).'],
                  ['approved', 'A value greater than zero in the Approved Price box.'],
                  ['tracking', 'The change type is set to "Tracking Only".'],
                  ['cancelled', 'Select the row(s) and click "Cancel/Restore" in the toolbar.'],
                  ['rejected', 'Click the "Rejected" button beside Approved Price in the Add / Edit form.'],
                ].map(([key, trigger]) => {
                  const m = STATUS_META[key];
                  return (
                    <tr key={key} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '0.5rem 0.75rem' }}>
                        <span style={{ padding: '2px 10px', borderRadius: '12px', backgroundColor: m.badgeBg, color: m.badgeText, fontWeight: '700', fontSize: '0.8rem', textDecoration: m.strike ? 'line-through' : 'none', textDecorationColor: '#dc2626' }}>{m.label}</span>
                      </td>
                      <td style={{ padding: '0.5rem 0.75rem', color: '#475569' }}>{trigger}</td>
                      <td style={{ padding: '0.5rem 0.75rem' }}>
                        <div style={{ width: '46px', height: '22px', borderRadius: '4px', backgroundColor: m.row, border: `1px solid ${m.dot}`, borderLeft: `4px solid ${m.dot}` }} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <h4 style={subHeadStyle}>Precedence</h4>
            <p style={paraStyle}>
              When several conditions are true, the most advanced stage wins:
              <strong> Tracking Only → Approved → Submitted → Pending</strong>. For example, an entry with both an Estimated $ and an
              Approved Price shows as <strong>Approved</strong>.
            </p>
            {noteBox('Cancelled and Rejected are "sticky" — once set, they are not overwritten by the cost boxes. Restore a cancelled entry with Cancel/Restore, or click the Rejected button again to clear a rejection (the status then re-derives from the boxes).')}

            <h4 style={subHeadStyle}>Color Legend (table rows)</h4>
            <ul style={bulletList}>
              <li><strong>Pending</strong> — white row.</li>
              <li><strong>Submitted</strong> — light green row.</li>
              <li><strong>Approved</strong> — green row.</li>
              <li><strong>Tracking</strong> — yellow row.</li>
              <li><strong>Cancelled</strong> — light gray row with red strikethrough text.</li>
              <li><strong>Rejected</strong> — dark gray row with red strikethrough text.</li>
            </ul>
            {tipBox('The current status is shown as a badge at the top of the Add / Edit form and updates live as you fill in the Estimated $, Submitted $, and Approved Price boxes.')}
          </div>
        );

      case 'toolbar':
        return (
          <div>
            <h3 style={headingStyle}>Toolbar Controls</h3>
            <p style={paraStyle}>The toolbar sits at the top of the dashboard and provides quick access to all major actions.</p>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', marginBottom: '1rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#f1f5f9' }}>
                  <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', borderBottom: '2px solid #e2e8f0', width: '140px' }}>Button</th>
                  <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>What it does</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['🔍 Search', 'Searches across all visible columns. Results update as you type.'],
                  ['View: (dropdown)', 'Filters the table to a single Discipline (Piping, Mechanical, etc.) with live per-discipline counts. Choose "All Disciplines" to clear.'],
                  ['➕ Add New', 'Opens the form to create a new Lump Sum or T&M entry.'],
                  ['📊 Columns', 'Show, hide, rearrange, and pin columns. Drag ⠿ or use ↑ ↓ to reorder. Click 📌 to freeze a column on the left while you scroll (up to 6). Your layout is saved on this browser.'],
                  ['↔️ Auto-fit', 'Resizes all columns to fit their content (measures header and cell text).'],
                  ['↩️ Reset', 'Reverts all columns to their default widths.'],
                  ['⤺ Undo', 'Undoes the last auto-fit or reset operation (appears only when history exists).'],
                  ['📥 Import', 'Import entries from an external file (coming soon).'],
                  ['📤 Export', 'Downloads the current filtered data as a CSV file.'],
                  ['📈 Analytics', 'Opens a tabbed analytics window: Overview (KPIs, value stream, status mix), By Type, By Discipline, By Status (with approval funnel), and Trends (cumulative $ over time). Slicers at the top filter by change type and status.'],
                  ['❓ Help', 'Opens this guide.'],
                ].map(([btn, desc], i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '0.5rem 0.75rem', fontWeight: '600', whiteSpace: 'nowrap' }}>{btn}</td>
                    <td style={{ padding: '0.5rem 0.75rem', color: '#475569' }}>{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h4 style={subHeadStyle}>Selection-Based Actions</h4>
            <p style={paraStyle}>When you select one or more rows via the checkboxes, additional action buttons appear in the toolbar:</p>
            <ul style={bulletList}>
              <li><strong>1 row selected</strong> — Edit, PDF, Cancel/Restore, and Delete buttons appear.</li>
              <li><strong>Multiple rows selected</strong> — PDF (batch), Cancel/Restore, and Delete (bulk) buttons appear.</li>
              <li><strong>Cancel/Restore</strong> — Marks selected entries as cancelled (or restores them if already cancelled). Cancelled entries remain visible with a red border and strikethrough.</li>
            </ul>
            {tipBox('Double-click any row to open it for editing directly, without needing to select it first.')}

            <h4 style={subHeadStyle}>Active Viewers</h4>
            <p style={paraStyle}>
              The toolbar shows an <strong>Active Viewers</strong> indicator displaying who else currently has this page open. Gray avatars are still here — their tab is in the background. A green dot means your connection is live.
            </p>
          </div>
        );

      case 'table':
        return (
          <div>
            <h3 style={headingStyle}>Table Features</h3>

            <h4 style={subHeadStyle}>Sorting</h4>
            <p style={paraStyle}>Click any column header to sort by that column. Click again to toggle between ascending and descending order. A sort indicator arrow appears next to the active sort column.</p>

            <h4 style={subHeadStyle}>Filtering</h4>
            <p style={paraStyle}>Every column has a filter icon in its header. Click the filter icon to open the advanced filter dropdown for that column.</p>
            <ul style={bulletList}>
              <li><strong>Text columns</strong> — Filter by checking/unchecking specific values, or use operators like Contains, Starts With, Ends With, Equals, Not Contains, Is Empty, Is Not Empty.</li>
              <li><strong>Numeric / Currency columns</strong> — Use operators like Equals, Greater Than, Less Than, Between, etc.</li>
              <li><strong>Date columns</strong> — Filter by Before, After, Between, or specific date values.</li>
              <li><strong>Combining filters</strong> — Filters across multiple columns work together (AND logic). A red badge shows the active filter count.</li>
            </ul>
            {tipBox('Click the red "N filters active" badge in the toolbar to clear all filters at once.')}

            <h4 style={subHeadStyle}>Column Management</h4>
            <ul style={bulletList}>
              <li><strong>Resize</strong> — Drag the right border of any column header to resize it.</li>
              <li><strong>Auto-fit</strong> — Click the Auto-fit button to automatically size all columns to fit their content.</li>
              <li><strong>Show / Hide / Rearrange / Freeze</strong> — Click the Columns button to toggle visibility, move columns with ⠿ or ↑ ↓, and pin 📌 Extra, Description, or any other column so it stays on the left while you scroll. Unpin clears all pins. Layout is saved on this browser.</li>
            </ul>

            <h4 style={subHeadStyle}>Row Selection</h4>
            <ul style={bulletList}>
              <li>Click the checkbox on any row to select it.</li>
              <li>Use the header checkbox to select or deselect all visible rows.</li>
              <li>Selected rows are highlighted and unlock toolbar actions (Edit, PDF, Delete).</li>
            </ul>

            <h4 style={subHeadStyle}>Available Columns</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.25rem 1.5rem', fontSize: '0.82rem', color: '#475569' }}>
              {[
                'JOB#', 'Discipline', 'Extra', 'Status', 'Change Type', 'Description',
                'Commencement Date', 'RFI No.', 'POCR #', 'POCN#', 'Estimated Hours',
                'Estimated $', 'Submitted Hours', 'Submitted $', 'Approved Price',
                'Maestro', 'Direct Total', 'Material Total', 'Subcontractor Total',
                'Equipment Total', 'Room & Board', 'Transport', 'Tax', 'CO Total',
                'Schedule (days)', 'Date Completed', 'Invoice #',
                'Invoice Date', 'Notes', 'Opened By', 'CTC/WBS'
              ].map((col, i) => (
                <div key={i} style={{ padding: '2px 0' }}>• {col}</div>
              ))}
            </div>
          </div>
        );

      case 'addentry':
        return (
          <div>
            <h3 style={headingStyle}>Adding / Editing an Entry</h3>
            <p style={paraStyle}>Click <strong>Add New</strong> in the toolbar to create an entry, or <strong>double-click a row</strong> to open the full form. To change just one field (Notes, Invoice # / Date, Date Completed, Approved Price, and the other simple columns), <strong>single-click that cell</strong> — no popup. The form still adapts based on the selected Change Type.</p>

            <h4 style={subHeadStyle}>Required Fields</h4>
            <ul style={bulletList}>
              <li><strong>Change Type</strong> — Lump Sum, T&M, Tracking Only, or Material Supply Only. Determines which cost sections appear and validation rules.</li>
              <li><strong>Discipline</strong> — The trade discipline (Piping, Mechanical, Electrical, etc.).</li>
              <li><strong>Description</strong> — A summary of what the change covers.</li>
              <li><strong>Estimated Hours</strong> — Required for Lump Sum (must be &gt; 0). For T&M and Material Supply Only, 0 is acceptable. Not required for Tracking Only.</li>
              <li><strong>Estimated $</strong> — Required for all types except Tracking Only.</li>
            </ul>

            <h4 style={subHeadStyle}>Auto-Generated / Auto-Calculated Fields</h4>
            <ul style={bulletList}>
              <li><strong>JOB#</strong> — Pulled from the project automatically.</li>
              <li><strong>Extra</strong> — Auto-generated sequentially per discipline (e.g., PI-001, ME-002).</li>
              <li><strong>Opened By</strong> — Auto-populated with your name on new entries.</li>
              <li><strong>Submitted Hours</strong> — Sum of hours from Direct line items (Lump Sum) or ST + OT + DT hours across all trades (T&M). Populated when details are entered.</li>
              <li><strong>Submitted $</strong> — Equals the Change Order Total (sum of all cost sections). Populated when details are entered.</li>
            </ul>
            {tipBox('Estimated Hours / $ are your initial SWAG. The Submitted Hours / $ are auto-calculated from the detail line items and represent the actual submitted amounts.')}

            <h4 style={subHeadStyle}>Status (set automatically)</h4>
            <p style={paraStyle}>
              A <strong>Status</strong> badge sits at the top of the form and updates live based on the cost boxes:
              Estimated $ &gt; 0 → <strong>Pending</strong>, Submitted $ &gt; 0 → <strong>Submitted</strong>, Approved Price &gt; 0 → <strong>Approved</strong>.
              Choosing the <strong>Tracking Only</strong> change type sets the status to <strong>Tracking</strong>.
            </p>
            <ul style={bulletList}>
              <li><strong>Rejected</strong> — Click the red "Rejected" button beside Approved Price to mark the change as rejected. Click it again to clear and re-derive the status.</li>
              <li><strong>Cancelled</strong> — Set from the table via the "Cancel/Restore" toolbar button (not inside the form).</li>
            </ul>
            <p style={paraStyle}>See the <strong>Status &amp; Colors</strong> help section for the full breakdown and color legend.</p>

            <h4 style={subHeadStyle}>Common Sections (Both Types)</h4>
            <ul style={bulletList}>
              <li><strong>Drawing List</strong> — Reference drawings affected by the change. Add Reference, Revision, and optional Link per drawing.</li>
              <li><strong>RFI List</strong> — Associated RFI references with revision and optional links.</li>
              <li><strong>Schedule Impact</strong> — Shop and Field days. The Total auto-calculates.</li>
              <li><strong>Clarification</strong> — Pre-populated default text about schedule impact assessment. Editable per entry.</li>
              <li><strong>Invoice #, Invoice Date, CTC/WBS, Notes</strong> — Tracking fields for downstream processing.</li>
            </ul>

            <h4 style={subHeadStyle}>Saving Options</h4>
            <ul style={bulletList}>
              <li><strong>Add Entry / Update</strong> — Saves the entry to the database.</li>
              <li><strong>Print PDF & Add Entry</strong> — Saves the entry AND downloads a formatted PDF in one click.</li>
            </ul>
            {noteBox('When editing, the form is pre-populated with all existing data. Changes are saved only when you click Update.')}
          </div>
        );

      case 'lumpsum':
        return (
          <div>
            <h3 style={headingStyle}>Lump Sum, Material Supply & Tracking Only Entries</h3>
            <p style={paraStyle}>
              These change types use the same internal cost-builder as T&M: enter your estimated
              <strong> costs</strong>, apply a <strong>markup</strong>, and the system generates the change value
              automatically. The cost detail is internal — the client-facing PDF shows only the resulting
              selling totals, never your cost basis or markup.
            </p>

            {noteBox('Internal only: the cost lines, hourly rates, unit prices, and markup % you enter here are NOT included in the downloaded/issued client PDF. The client PDF shows the category selling totals and the Change Order Total exactly as before.')}

            <h4 style={subHeadStyle}>Cost-Builder Sections</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', marginBottom: '1rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#f0f9ff' }}>
                  <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '2px solid #bae6fd' }}>Section</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '2px solid #bae6fd' }}>Fields per Line</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '2px solid #bae6fd' }}>Markup</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Labour (by Trade)', 'Trade, Crew, Total Hours (auto-split ST/OT/DT) + Rates', 'No (cost = hours x rate)'],
                  ['Material', 'Description, Qty, Unit Price', 'Yes'],
                  ['Subcontractors', 'Description, Qty, Unit Price', 'Yes'],
                  ['Equipment', 'Description, Qty, Unit Price', 'Yes'],
                  ['Room & Board', 'Auto: per-diem x days/week ÷ hours/week x total hours', 'No'],
                  ['Transport', 'Auto: per-diem x days/week ÷ hours/week x total hours', 'No'],
                  ['Self-Assessed Tax', 'Single dollar amount', 'No'],
                ].map(([sec, fields, mk], i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '0.5rem', fontWeight: '600' }}>{sec}</td>
                    <td style={{ padding: '0.5rem' }}>{fields}</td>
                    <td style={{ padding: '0.5rem', color: '#64748b' }}>{mk}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h4 style={subHeadStyle}>Auto-Calculated Totals</h4>
            <div style={{ backgroundColor: '#f8fafc', padding: '0.75rem 1rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontFamily: 'monospace', fontSize: '0.85rem', color: '#334155', marginBottom: '0.5rem' }}>
              Change Order Total = Labour + Material + Subcontractors + Equipment + Room & Board + Transport + Tax
            </div>
            <div style={{ backgroundColor: '#f8fafc', padding: '0.75rem 1rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontFamily: 'monospace', fontSize: '0.85rem', color: '#334155', marginBottom: '0.75rem' }}>
              Submitted Price = Change Order Total &nbsp;|&nbsp; Submitted Hours = Sum of all trade hours
            </div>
            {tipBox('Room & Board and Transport are auto-calculated from the project Configuration (per-diem ÷ hours/week → $/hr) multiplied by the change order\u2019s total hours. Set the per-diems and work calendar via the Configuration button at the top of the Change Log.')}
            {noteBox('Estimated $ is a manual estimate (SWAG) and must be greater than zero for Lump Sum and Material Supply Only. Estimated Hours can be 0 for these types since the builder derives hours from the trades.')}
            {tipBox('Older entries created with the previous "type the selling cost directly" fields are converted automatically with markup set to 0% so the original totals stay identical.')}
          </div>
        );

      case 'tm':
        return (
          <div>
            <h3 style={headingStyle}>T&M (Time & Materials) Entries</h3>
            <p style={paraStyle}>T&M entries track costs by trade labour hours and rates, plus material, subcontractors, and equipment with a configurable markup percentage.</p>

            <h4 style={subHeadStyle}>Markup Percentage</h4>
            <p style={paraStyle}>
              A global markup % (default 8.75%) is applied to Material, Subcontractors, and Equipment line items.
              Change this value at the top of the cost section to adjust all markup calculations at once.
            </p>

            <h4 style={subHeadStyle}>Labour by Trade</h4>
            <p style={paraStyle}>Each trade entry captures three rate categories:</p>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#fef9c3' }}>
                  <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '2px solid #fde68a' }}>Category</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '2px solid #fde68a' }}>Fields</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '2px solid #fde68a' }}>Typical Multiplier</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Straight Time (ST)', 'Hours + Rate', '1.0x base rate'],
                  ['Overtime (OT)', 'Hours + Rate', '1.5x base rate'],
                  ['Double Time (DT)', 'Hours + Rate', '2.0x base rate'],
                ].map(([cat, fields, mult], i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '0.5rem', fontWeight: '600' }}>{cat}</td>
                    <td style={{ padding: '0.5rem' }}>{fields}</td>
                    <td style={{ padding: '0.5rem', color: '#64748b' }}>{mult}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={paraStyle}>Trade Total = (ST Hours x ST Rate) + (OT Hours x OT Rate) + (DT Hours x DT Rate)</p>

            <h4 style={subHeadStyle}>Material, Subcontractors, Equipment</h4>
            <p style={paraStyle}>Each of these sections works the same way:</p>
            <ul style={bulletList}>
              <li>Enter a <strong>Description</strong>, <strong>Quantity</strong>, and <strong>Unit Price / Hourly Rate</strong>.</li>
              <li>The markup % is automatically applied to each line: <strong>Line Total = Qty x Unit Price x (1 + Markup%)</strong>.</li>
              <li>The section total is the sum of all line totals including markup.</li>
            </ul>

            <h4 style={subHeadStyle}>Trade Dropdown &amp; Auto-Split Hours</h4>
            <p style={paraStyle}>
              Pick a <strong>Trade</strong> from the dropdown (configured trades) to auto-fill its ST/OT/DT rates and per-diems,
              or type a custom trade name. Enter a single <strong>Hours</strong> (total) and the system fills Straight Time up to
              that trade&rsquo;s weekly cap (from Configuration), then Overtime, then Double Time, repeating week over week.
              There is no crew field &mdash; proration is by the trade&rsquo;s calendar only. You can also type ST/OT/DT directly
              and the total sums up automatically.
            </p>

            <h4 style={subHeadStyle}>Room &amp; Board and Transport</h4>
            <p style={paraStyle}>
              <strong>Room &amp; Board</strong> and <strong>Transport</strong> are auto-calculated <strong>per trade</strong> from
              Configuration: each trade&rsquo;s per-diem is converted to an hourly cost (per-diem &times; days/week &divide; hours/week)
              and multiplied by that trade&rsquo;s hours, then summed across all trades. Both are added to the Change Order Total with
              no markup. Adjust per-trade rates, per-diems, and calendars via the <strong>Configuration</strong> button at the top of
              the Change Log.
            </p>

            <h4 style={subHeadStyle}>Auto-Calculated Totals</h4>
            <div style={{ backgroundColor: '#f8fafc', padding: '0.75rem 1rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontFamily: 'monospace', fontSize: '0.85rem', color: '#334155', marginBottom: '0.5rem' }}>
              Change Order Total = Labour + Material + Subcontractor + Equipment + Room & Board + Transport
            </div>
            <div style={{ backgroundColor: '#f8fafc', padding: '0.75rem 1rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontFamily: 'monospace', fontSize: '0.85rem', color: '#334155', marginBottom: '0.5rem' }}>
              Submitted Price = Change Order Total
            </div>
            <div style={{ backgroundColor: '#f8fafc', padding: '0.75rem 1rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontFamily: 'monospace', fontSize: '0.85rem', color: '#334155', marginBottom: '0.75rem' }}>
              Submitted Hours = Sum of (ST Hours + OT Hours + DT Hours) across all trades
            </div>
            {noteBox('For T&M entries, 0 labour hours is acceptable. Estimated price must be greater than zero. Markup is only applied to Material, Subcontractors, and Equipment — never to labour, Room & Board, or Transport.')}
          </div>
        );

      case 'pdf':
        return (
          <div>
            <h3 style={headingStyle}>PDF Export</h3>
            <p style={paraStyle}>Generate formatted PDF documents for change orders to share with clients or keep on file.</p>

            <h4 style={subHeadStyle}>How to Generate a PDF</h4>
            <ol style={bulletList}>
              <li>Select a row in the table, then click the <strong>PDF</strong> button in the toolbar.</li>
              <li>Or, when adding/editing an entry, click <strong>"Print PDF & Add Entry"</strong> to save and download in one step.</li>
              <li>For bulk export, select multiple rows and click <strong>"PDF (N)"</strong> to download one PDF per selected entry.</li>
            </ol>

            <h4 style={subHeadStyle}>Lump Sum PDF Layout (client-facing)</h4>
            <ul style={bulletList}>
              <li>Header with company logo, project name, job number, and extra number.</li>
              <li>Drawing List and RFI List tables.</li>
              <li>Construction Costs table showing <strong>selling totals only</strong>: Direct, Material, Subcontractors, Equipment, Room & Board, Transport, Tax.</li>
              <li><strong>Internal detail is excluded</strong> — no hourly rates, unit prices, hours breakdown, or markup % appear on this client PDF.</li>
              <li>Schedule impact (Shop Days, Field Days, Total).</li>
              <li>Clarification section with the default or custom text.</li>
            </ul>

            <h4 style={subHeadStyle}>T&M PDF Layout</h4>
            <ul style={bulletList}>
              <li>Header with company logo, project name, job number, and extra number.</li>
              <li>Drawing and RFI references.</li>
              <li>Labour section with trade-by-trade breakdown (ST/OT/DT hours, rates, and totals).</li>
              <li>Material, Subcontractors, and Equipment sections with quantity, unit price, markup %, and totals.</li>
              <li>Other Costs section listing <strong>Room & Board</strong> and <strong>Transport</strong> when present.</li>
              <li>Summary with Change Order Total, schedule impact, and clarification.</li>
            </ul>
            {tipBox('The PDF file is named using the extra number for easy identification (e.g., "Change_Order_PIPE-001.pdf").')}
          </div>
        );

      case 'shortcuts':
        return (
          <div>
            <h3 style={headingStyle}>Tips & Shortcuts</h3>

            <h4 style={subHeadStyle}>Keyboard & Mouse Shortcuts</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', marginBottom: '1rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#f1f5f9' }}>
                  <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', borderBottom: '2px solid #e2e8f0', width: '200px' }}>Action</th>
                  <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>How</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Edit one cell', 'Single-click Notes, Invoice, dates, Approved Price, Description, Estimated Hours/$, RFI/POCR/POCN, or CTC/WBS'],
                  ['Open the full form', 'Double-click the row (not an inline cell)'],
                  ['Select all rows', 'Click the header checkbox'],
                  ['Clear all filters', 'Click the red filter badge in the toolbar'],
                  ['Resize a column', 'Drag the right edge of a column header'],
                  ['Sort a column', 'Click the column header text'],
                  ['Open column filter', 'Click the filter icon in any column header'],
                ].map(([action, how], i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '0.5rem 0.75rem', fontWeight: '600' }}>{action}</td>
                    <td style={{ padding: '0.5rem 0.75rem', color: '#475569' }}>{how}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h4 style={subHeadStyle}>Best Practices</h4>
            <ul style={bulletList}>
              <li>Always select the correct <strong>Change Type</strong> before entering costs — switching types changes which cost sections appear.</li>
              <li>Use <strong>Tracking Only</strong> for items that need to be documented but don't have cost details yet.</li>
              <li>Use <strong>Material Supply Only</strong> when there's no labour component — the system won't require hours.</li>
              <li>Use <strong>Cancel/Restore</strong> instead of deleting entries you want to keep for historical record.</li>
              <li>For T&M entries, don't forget to fill in <strong>Room & Board</strong> if applicable.</li>
              <li>Use the <strong>Drawing List</strong> and <strong>RFI List</strong> to link reference documents — they appear on the PDF.</li>
              <li>The <strong>Clarification</strong> field contains default schedule impact language — customize it per change if needed.</li>
              <li>Use <strong>Analytics</strong> to view the discipline breakdown and project-level totals without exporting.</li>
              <li>Use <strong>Auto-fit</strong> after loading data to optimize column widths for readability.</li>
              <li>For T&M entries, set the <strong>Markup %</strong> before adding material/sub/equipment lines — it applies retroactively.</li>
            </ul>

            <h4 style={subHeadStyle}>Data Flow</h4>
            <div style={{ backgroundColor: '#f8fafc', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.85rem', color: '#334155', lineHeight: '1.8' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                <span style={{ padding: '0.25rem 0.75rem', backgroundColor: '#dbeafe', borderRadius: '4px', fontWeight: '600' }}>Add Entry</span>
                <span>→</span>
                <span style={{ padding: '0.25rem 0.75rem', backgroundColor: '#e0e7ff', borderRadius: '4px', fontWeight: '600' }}>Auto-Calc Totals</span>
                <span>→</span>
                <span style={{ padding: '0.25rem 0.75rem', backgroundColor: '#fef3c7', borderRadius: '4px', fontWeight: '600' }}>Submit for Approval</span>
                <span>→</span>
                <span style={{ padding: '0.25rem 0.75rem', backgroundColor: '#d1fae5', borderRadius: '4px', fontWeight: '600' }}>Enter Approved Price</span>
                <span>→</span>
                <span style={{ padding: '0.25rem 0.75rem', backgroundColor: '#f0fdf4', borderRadius: '4px', fontWeight: '600' }}>Generate PDF</span>
                <span>→</span>
                <span style={{ padding: '0.25rem 0.75rem', backgroundColor: '#dcfce7', borderRadius: '4px', fontWeight: '600' }}>Invoice & Complete</span>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: '500px', maxHeight: '70vh' }}>
      {/* Sidebar Navigation */}
      <div style={{ width: '180px', borderRight: '1px solid #e2e8f0', overflowY: 'auto', flexShrink: 0 }}>
        {sections.map(s => (
          <div
            key={s.id}
            onClick={() => setActiveSection(s.id)}
            style={{
              padding: '0.65rem 0.75rem',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: activeSection === s.id ? '700' : '500',
              color: activeSection === s.id ? '#1e40af' : '#475569',
              backgroundColor: activeSection === s.id ? '#eff6ff' : 'transparent',
              borderLeft: activeSection === s.id ? '3px solid #3b82f6' : '3px solid transparent',
              transition: 'all 0.15s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <span style={{ fontSize: '1rem' }}>{s.icon}</span>
            <span>{s.label}</span>
          </div>
        ))}
      </div>
      {/* Content */}
      <div style={{ flex: 1, padding: '1.25rem 1.5rem', overflowY: 'auto' }}>
        {renderContent()}
      </div>
    </div>
  );
});

// A "legacy" cost line is one created by the old non-T&M form, where the user
// typed a finished selling_cost directly (no quantity/unit_price/trade rate fields).
const isLegacyMarkupItem = (item) =>
  item && item.selling_cost !== undefined && item.selling_cost !== '' &&
  item.quantity === undefined && item.unit_price === undefined;

// Detect a non-T&M entry that still uses the old selling-cost line shape and has
// not yet been moved into the shared cost-builder (tm_labour_items / qty+unit_price).
const isLegacyNonTMEntry = (data) => {
  if (!data || data.billing_type === 'T&M') return false;
  const tm = Array.isArray(data.tm_labour_items) ? data.tm_labour_items : [];
  const labour = Array.isArray(data.labour_items) ? data.labour_items : [];
  const mat = Array.isArray(data.material_items) ? data.material_items : [];
  const sub = Array.isArray(data.subcontractor_items) ? data.subcontractor_items : [];
  const equip = Array.isArray(data.equipment_items) ? data.equipment_items : [];
  const hasLegacyLabour = labour.length > 0 && tm.length === 0;
  const hasLegacyMarkup = [mat, sub, equip].some(arr => arr.some(isLegacyMarkupItem));
  return hasLegacyLabour || hasLegacyMarkup;
};

// Convert an old non-T&M entry into the shared cost-builder shape WITHOUT changing
// any dollar totals: markup is forced to 0% and each old line is mapped 1:1 so the
// resulting Change Order Total is identical to what was originally saved.
const migrateLegacyNonTMEntry = (data) => {
  if (!isLegacyNonTMEntry(data)) return data;
  const out = { ...data, markup_percentage: 0 };

  const labour = Array.isArray(data.labour_items) ? data.labour_items : [];
  if (labour.length > 0 && (!Array.isArray(data.tm_labour_items) || data.tm_labour_items.length === 0)) {
    out.tm_labour_items = labour.map((it) => {
      const hours = parseFloat(it.hours) || 0;
      const sell = parseFloat(it.selling_cost) || 0;
      // Preserve the dollar total via hours x rate. If hours are 0 but a cost
      // exists, use a single phantom hour so the amount is not lost.
      const useHours = hours > 0 ? hours : (sell !== 0 ? 1 : 0);
      const rate = useHours > 0 ? sell / useHours : 0;
      return {
        trade_name: it.description || '',
        st_hours: useHours || '', st_rate: useHours > 0 ? rate : '',
        ot_hours: '', ot_rate: '', dt_hours: '', dt_rate: '',
      };
    });
    out.labour_items = [];
  }

  const convertMarkupList = (arr) => (Array.isArray(arr) ? arr : []).map((it) =>
    isLegacyMarkupItem(it)
      ? { description: it.description || '', quantity: 1, unit_price: parseFloat(it.selling_cost) || 0 }
      : it
  );
  out.material_items = convertMarkupList(data.material_items);
  out.subcontractor_items = convertMarkupList(data.subcontractor_items);
  out.equipment_items = convertMarkupList(data.equipment_items);

  return out;
};

// ============================================
// CONFIGURATION FORM (per-project Change Log settings)
// ============================================
const ConfigForm = memo(({ initialConfig, onSave, onCancel }) => {
  const [form, setForm] = useState(() => ({
    ...DEFAULT_CONFIG,
    ...(initialConfig || {}),
    trades: Array.isArray(initialConfig?.trades) ? initialConfig.trades.map(t => ({ ...t })) : [],
  }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const addTrade = () => {
    setForm(prev => ({ ...prev, trades: [...(prev.trades || []), makeTradeFromDefaults(prev, '')] }));
  };
  const removeTrade = (idx) => {
    setForm(prev => ({ ...prev, trades: (prev.trades || []).filter((_, i) => i !== idx) }));
  };
  const updateTrade = (idx, field, value) => {
    setForm(prev => ({
      ...prev,
      trades: (prev.trades || []).map((t, i) => i === idx ? { ...t, [field]: value } : t),
    }));
  };

  const labelStyle = { display: 'block', marginBottom: '0.2rem', fontWeight: '500', color: '#374151', fontSize: '0.78rem' };
  const inputStyle = { width: '100%', padding: '0.45rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', boxSizing: 'border-box' };
  const sectionHeaderStyle = { fontWeight: '700', fontSize: '0.95rem', color: '#1e293b', marginTop: '0.75rem', marginBottom: '0.5rem', borderBottom: '2px solid #e2e8f0', paddingBottom: '0.25rem' };
  const grid3 = { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.6rem' };

  const numField = (obj, field, label, onCh, placeholder = '0', step = '0.5') => (
    <div>
      <label style={labelStyle}>{label}</label>
      <input type="number" step={step} value={obj[field] ?? ''} onChange={(e) => onCh(field, e.target.value)} style={inputStyle} placeholder={placeholder} />
    </div>
  );

  // shared editor for calendar + rates + per-diems (used by Defaults and each Trade)
  const renderCfgFields = (obj, onCh) => {
    const { hoursPerWeek, rbPerHour, transportPerHour } = computePerHourCosts(obj);
    return (
      <>
        <div style={{ fontSize: '0.72rem', fontWeight: '600', color: '#64748b', margin: '0.4rem 0 0.2rem' }}>Work Calendar (per week)</div>
        <div style={grid3}>
          {numField(obj, 'straight_hours_per_week', 'ST hrs/wk', onCh, '40')}
          {numField(obj, 'overtime_hours_per_week', 'OT hrs/wk', onCh, '20')}
          {numField(obj, 'double_time_hours_per_week', 'DT hrs/wk', onCh, '0')}
          {numField(obj, 'days_per_week', 'Days/wk', onCh, '7', '1')}
        </div>
        <div style={{ fontSize: '0.72rem', fontWeight: '600', color: '#64748b', margin: '0.5rem 0 0.2rem' }}>Hourly Rates</div>
        <div style={grid3}>
          {numField(obj, 'straight_rate', 'ST rate ($/hr)', onCh, '0.00', '0.01')}
          {numField(obj, 'overtime_rate', 'OT rate ($/hr)', onCh, '0.00', '0.01')}
          {numField(obj, 'double_time_rate', 'DT rate ($/hr)', onCh, '0.00', '0.01')}
        </div>
        <div style={{ fontSize: '0.72rem', fontWeight: '600', color: '#64748b', margin: '0.5rem 0 0.2rem' }}>Per-Diems</div>
        <div style={grid3}>
          {numField(obj, 'room_board_per_diem', 'Room & Board ($/day)', onCh, '0.00', '0.01')}
          {numField(obj, 'transport_per_diem', 'Transport ($/day)', onCh, '0.00', '0.01')}
        </div>
        <div style={{ fontSize: '0.72rem', color: '#1e40af', marginTop: '0.4rem' }}>
          {hoursPerWeek} hrs/wk &nbsp;|&nbsp; R&amp;B <strong>${fmtCurrency(rbPerHour)}/hr</strong> &nbsp;|&nbsp; Transport <strong>${fmtCurrency(transportPerHour)}/hr</strong>
        </div>
      </>
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const numv = (v) => parseFloat(v) || 0;
    onSave({
      straight_hours_per_week: numv(form.straight_hours_per_week),
      overtime_hours_per_week: numv(form.overtime_hours_per_week),
      double_time_hours_per_week: numv(form.double_time_hours_per_week),
      days_per_week: numv(form.days_per_week),
      room_board_per_diem: numv(form.room_board_per_diem),
      transport_per_diem: numv(form.transport_per_diem),
      straight_rate: numv(form.straight_rate),
      overtime_rate: numv(form.overtime_rate),
      double_time_rate: numv(form.double_time_rate),
      trades: (form.trades || []).map(t => ({
        name: (t.name || '').trim(),
        straight_hours_per_week: numv(t.straight_hours_per_week),
        overtime_hours_per_week: numv(t.overtime_hours_per_week),
        double_time_hours_per_week: numv(t.double_time_hours_per_week),
        days_per_week: numv(t.days_per_week),
        straight_rate: numv(t.straight_rate),
        overtime_rate: numv(t.overtime_rate),
        double_time_rate: numv(t.double_time_rate),
        room_board_per_diem: numv(t.room_board_per_diem),
        transport_per_diem: numv(t.transport_per_diem),
      })),
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <p style={{ fontSize: '0.85rem', color: '#475569', marginTop: 0 }}>
        Define the <strong>trades</strong> for this project. Each trade has its own work calendar
        (used to auto-split a trade's total hours into ST/OT/DT), its own ST/OT/DT hourly rates, and its
        own Room &amp; Board / Transport per-diems. When a trade is picked in a change order, these values
        auto-fill (and can still be overridden there). The <strong>Default values</strong> below seed new
        trades and are used as the fallback for any custom trade typed directly into a change order.
      </p>

      <div style={sectionHeaderStyle}>Default values (fallback / seed for new trades)</div>
      <div style={{ padding: '0.5rem 0.75rem', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
        {renderCfgFields(form, (field, value) => setForm(prev => ({ ...prev, [field]: value })))}
      </div>

      <div style={{ ...sectionHeaderStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Trades ({(form.trades || []).length})</span>
        <button type="button" onClick={addTrade} style={{
          padding: '0.3rem 0.75rem', backgroundColor: '#28a745', color: 'white',
          border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '500'
        }}>+ Add Trade</button>
      </div>

      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.4rem' }}>
        One row per trade. <strong>R&amp;B/hr</strong> and <strong>Transport/hr</strong> are computed from each per-diem
        (per-diem &times; days/wk &divide; total hrs/wk).
      </div>
      {(form.trades || []).length === 0 ? (
        <div style={{ color: '#9ca3af', fontSize: '0.85rem', padding: '0.5rem 0', fontStyle: 'italic' }}>
          No trades configured. Click "+ Add Trade" to define one (seeded from the Default values above).
        </div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid #fde68a', borderRadius: '8px' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '900px', fontSize: '0.8rem' }}>
            <thead>
              <tr style={{ backgroundColor: '#fffbeb' }}>
                {[
                  { l: 'Trade', w: 160, a: 'left' }, { l: 'ST h/wk', w: 60 }, { l: 'OT h/wk', w: 60 }, { l: 'DT h/wk', w: 60 }, { l: 'Days/wk', w: 60 },
                  { l: 'ST $/hr', w: 70 }, { l: 'OT $/hr', w: 70 }, { l: 'DT $/hr', w: 70 },
                  { l: 'R&B $/day', w: 75 }, { l: 'Transp $/day', w: 80 },
                  { l: 'R&B/hr', w: 64 }, { l: 'Transp/hr', w: 70 }, { l: '', w: 34 },
                ].map((h, i) => (
                  <th key={i} style={{ padding: '0.4rem 0.3rem', fontSize: '0.7rem', fontWeight: '700', color: '#374151', borderBottom: '2px solid #fde68a', textAlign: h.a || 'center', width: h.w, whiteSpace: 'nowrap' }}>{h.l}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(form.trades || []).map((t, idx) => {
                const { rbPerHour, transportPerHour } = computePerHourCosts(t);
                const cellInput = (field, step = '0.5') => (
                  <input type="number" step={step} value={t[field] ?? ''} onChange={(e) => updateTrade(idx, field, e.target.value)}
                    style={{ width: '100%', padding: '0.3rem 0.25rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.78rem', boxSizing: 'border-box', textAlign: 'right' }} />
                );
                const tdc = { padding: '0.25rem 0.3rem' };
                return (
                  <tr key={idx} style={{ borderBottom: '1px solid #fef3c7' }}>
                    <td style={tdc}>
                      <input type="text" value={t.name || ''} onChange={(e) => updateTrade(idx, 'name', e.target.value)}
                        placeholder="e.g. Pipe Fitter Field"
                        style={{ width: '100%', padding: '0.3rem 0.4rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.8rem', fontWeight: '600', boxSizing: 'border-box' }} />
                    </td>
                    <td style={tdc}>{cellInput('straight_hours_per_week')}</td>
                    <td style={tdc}>{cellInput('overtime_hours_per_week')}</td>
                    <td style={tdc}>{cellInput('double_time_hours_per_week')}</td>
                    <td style={tdc}>{cellInput('days_per_week', '1')}</td>
                    <td style={tdc}>{cellInput('straight_rate', '0.01')}</td>
                    <td style={tdc}>{cellInput('overtime_rate', '0.01')}</td>
                    <td style={tdc}>{cellInput('double_time_rate', '0.01')}</td>
                    <td style={tdc}>{cellInput('room_board_per_diem', '0.01')}</td>
                    <td style={tdc}>{cellInput('transport_per_diem', '0.01')}</td>
                    <td style={{ ...tdc, textAlign: 'right', color: '#1e40af', fontWeight: '600', whiteSpace: 'nowrap' }}>${fmtCurrency(rbPerHour)}</td>
                    <td style={{ ...tdc, textAlign: 'right', color: '#1e40af', fontWeight: '600', whiteSpace: 'nowrap' }}>${fmtCurrency(transportPerHour)}</td>
                    <td style={{ ...tdc, textAlign: 'center' }}>
                      <button type="button" onClick={() => removeTrade(idx)} title="Remove trade" style={{
                        padding: '0.2rem 0.4rem', backgroundColor: '#fef3f3', color: '#dc3545',
                        border: '1px solid #fca5a5', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem'
                      }}>×</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="primary">Save Configuration</Button>
      </div>
    </form>
  );
});

const EntryForm = memo(({ initialData, onSubmit, onCancel, jobNumber, jobName, projectId, currentUserName, instance, accounts, onPrintPDF, config }) => {
  const [formData, setFormData] = useState(() => {
    if (initialData) {
      const migrated = migrateLegacyNonTMEntry(initialData);
      return {
        ...migrated,
        drawing_list: Array.isArray(migrated.drawing_list) ? migrated.drawing_list : [],
        rfi_list: Array.isArray(migrated.rfi_list) ? migrated.rfi_list : [],
        labour_items: Array.isArray(migrated.labour_items) ? migrated.labour_items : [],
        indirect_items: Array.isArray(migrated.indirect_items) ? migrated.indirect_items : [],
        material_items: Array.isArray(migrated.material_items) ? migrated.material_items : [],
        tm_labour_items: Array.isArray(migrated.tm_labour_items) ? migrated.tm_labour_items : [],
        subcontractor_items: Array.isArray(migrated.subcontractor_items) ? migrated.subcontractor_items : [],
        equipment_items: Array.isArray(migrated.equipment_items) ? migrated.equipment_items : [],
        markup_percentage: migrated.markup_percentage ?? 8.75,
        clarification: migrated.clarification || DEFAULT_CLARIFICATION,
      };
    }
    const now = new Date();
    const timestamp = now.toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
    return {
      billing_type: '', discipline: '', new_extra_number: '',
      commencement_date: now.toISOString().split('T')[0],
      rfi_number: '', pocr_number: '', pocn_number: '',
      estimated_hours: '', estimated_price: '',
      submitted_hours: '', description: '',
      submitted_price: '', approved_price: '',
      maestro_status: '', status: 'pending',
      date_completed: '', invoice_number: '', invoice_date: '',
      notes: '', opened_by: `${currentUserName} - ${timestamp}`, ctc_wbs: '',
      drawing_list: [], rfi_list: [],
      labour_items: [], indirect_items: [], material_items: [],
      tm_labour_items: [], subcontractor_items: [], equipment_items: [],
      self_assessed_tax: '', markup_percentage: 8.75,
      labour_total: '', indirect_total: '', material_total: '', subcontractor_total: '', equipment_total: '',
      change_order_total: '', room_and_board: '', transport: '',
      schedule_shop_days: '', schedule_field_days: '', schedule_total_days: '',
      clarification: DEFAULT_CLARIFICATION,
    };
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [loadingExtra, setLoadingExtra] = useState(false);
  const [wasMigrated] = useState(() => isLegacyNonTMEntry(initialData));
  const isTM = formData.billing_type === 'T&M';
  const markupPct = parseFloat(formData.markup_percentage) || 8.75;

  useEffect(() => {
    const shopD = parseFloat(formData.schedule_shop_days) || 0;
    const fieldD = parseFloat(formData.schedule_field_days) || 0;

    // All change types now build cost from the shared cost-builder:
    // Labour by trade (ST/OT/DT) + Material/Subcontractors/Equipment (qty x unit + markup)
    // + Room & Board. Non-T&M change types additionally include Self-Assessed Tax.
    const calcRow = (h, r) => (parseFloat(h) || 0) * (parseFloat(r) || 0);
    const tmTrades = formData.tm_labour_items || [];
    const lt = tmTrades.reduce((s, t) => s + calcRow(t.st_hours, t.st_rate) + calcRow(t.ot_hours, t.ot_rate) + calcRow(t.dt_hours, t.dt_rate), 0);

    const totalHours = tmTrades.reduce((s, t) =>
      s + (parseFloat(t.st_hours) || 0) + (parseFloat(t.ot_hours) || 0) + (parseFloat(t.dt_hours) || 0), 0);

    const calcMarkupTotal = (items) => (items || []).reduce((s, item) => {
      const base = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
      return s + base + (base * markupPct / 100);
    }, 0);

    const mt = calcMarkupTotal(formData.material_items);
    const st = calcMarkupTotal(formData.subcontractor_items);
    const et = calcMarkupTotal(formData.equipment_items);

    // Room & Board and Transport are auto-derived PER TRADE from Configuration:
    // for each trade, per_diem * days_per_week / hours_per_week -> $/hr, then x that trade's hours.
    const round2 = (n) => Math.round((n || 0) * 100) / 100;
    let rbAcc = 0, transportAcc = 0;
    tmTrades.forEach((t) => {
      const tradeCfg = resolveTradeConfig(t, config);
      const { rbPerHour: rbPH, transportPerHour: trPH } = computePerHourCosts(tradeCfg);
      const tradeHours = (parseFloat(t.st_hours) || 0) + (parseFloat(t.ot_hours) || 0) + (parseFloat(t.dt_hours) || 0);
      rbAcc += rbPH * tradeHours;
      transportAcc += trPH * tradeHours;
    });
    const rb = round2(rbAcc);
    const transport = round2(transportAcc);

    const tax = isTM ? 0 : (parseFloat(formData.self_assessed_tax) || 0);
    const co = lt + mt + st + et + rb + transport + tax;

    setFormData(prev => ({
      ...prev,
      submitted_hours: totalHours,
      submitted_price: co,
      labour_total: lt,
      material_total: mt,
      subcontractor_total: st,
      equipment_total: et,
      room_and_board: rb,
      transport: transport,
      change_order_total: co,
      schedule_total_days: shopD + fieldD,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.material_items, formData.subcontractor_items, formData.equipment_items,
      formData.self_assessed_tax, formData.tm_labour_items,
      formData.markup_percentage, formData.schedule_shop_days, formData.schedule_field_days, isTM, markupPct, config]);

  // Auto-derive the lifecycle status from the cost boxes / change type.
  // Cancelled and Rejected are manual terminal states and are never overwritten here.
  useEffect(() => {
    setFormData(prev => {
      if (prev.status === 'cancelled' || prev.status === 'rejected') return prev;
      const derived = deriveStatus(prev);
      if (derived === prev.status) return prev;
      return { ...prev, status: derived };
    });
  }, [formData.billing_type, formData.approved_price, formData.submitted_price, formData.estimated_price]);

  const isRejected = formData.status === 'rejected';
  const isCancelledEntry = formData.status === 'cancelled';
  const toggleRejected = () => {
    setFormData(prev => prev.status === 'rejected'
      ? { ...prev, status: deriveStatus(prev) }
      : { ...prev, status: 'rejected' });
  };
  const currentStatusKey = getEntryStatus(formData);
  const currentStatusMeta = STATUS_META[currentStatusKey] || STATUS_META.pending;

  const fetchNextExtra = useCallback(async (discipline) => {
    if (!projectId || !discipline) return;
    setLoadingExtra(true);
    try {
      const result = await authenticatedApiCall(instance, accounts, 'GET', `/control-chart/${projectId}/next-extra?discipline=${encodeURIComponent(discipline)}`);
      if (result.data?.success) {
        setFormData(prev => ({ ...prev, new_extra_number: result.data.nextExtra }));
      }
    } catch (err) {
      console.error('Error fetching next extra number:', err);
    } finally {
      setLoadingExtra(false);
    }
  }, [projectId, instance, accounts]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors(prev => { const next = { ...prev }; delete next[name]; return next; });
    }
    if (name === 'discipline' && value && !initialData) {
      fetchNextExtra(value);
    }
  };

  const isTrackingOnly = formData.billing_type === 'Tracking Only';
  const isMaterialSupply = formData.billing_type === 'Material Supply Only';

  const validate = () => {
    const errors = {};
    if (!formData.billing_type) errors.billing_type = 'Change type is required';
    if (!formData.discipline) errors.discipline = 'Discipline is required';
    if (!formData.description) errors.description = 'Description is required';
    if (!isTrackingOnly) {
      if (isTM || isMaterialSupply) {
        if (formData.estimated_hours !== '' && formData.estimated_hours !== 0 && parseFloat(formData.estimated_hours) < 0) {
          errors.estimated_hours = 'Estimated hours cannot be negative';
        }
      } else {
        if (!formData.estimated_hours || parseFloat(formData.estimated_hours) <= 0) errors.estimated_hours = 'Estimated hours is required';
      }
      if (!formData.estimated_price || parseFloat(formData.estimated_price) <= 0) errors.estimated_price = 'Estimated price is required';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit({ ...formData });
  };

  const handlePrintAndSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const submitData = { ...formData };
    onSubmit(submitData);
    setTimeout(() => {
      onPrintPDF({ ...submitData, job_number: jobNumber });
    }, 500);
  };

  const inputStyle = {
    width: '100%', padding: '0.75rem', border: '1px solid #d1d5db',
    borderRadius: '6px', fontSize: '0.9rem', boxSizing: 'border-box'
  };
  const errorInputStyle = { ...inputStyle, border: '1px solid #dc3545', backgroundColor: '#fff5f5' };
  const labelStyle = { display: 'block', marginBottom: '0.25rem', fontWeight: '500', color: '#374151', fontSize: '0.9rem' };
  const requiredStar = { color: '#dc3545', marginLeft: '2px' };
  const sectionHeaderStyle = { fontWeight: '700', fontSize: '0.95rem', color: '#1e293b', marginTop: '1.5rem', marginBottom: '0.5rem', borderBottom: '2px solid #e2e8f0', paddingBottom: '0.25rem' };

  return (
    <form onSubmit={handleSubmit}>
      {/* Change Type */}
      <div style={{ marginBottom: '1.25rem', padding: '1rem', backgroundColor: CHANGE_TYPE_COLORS[formData.billing_type]?.bg || '#f8fafc', borderRadius: '8px', border: `1px solid ${CHANGE_TYPE_COLORS[formData.billing_type]?.border || '#e2e8f0'}` }}>
        <label style={{ ...labelStyle, fontSize: '1rem', marginBottom: '0.5rem' }}>
          Change Type<span style={requiredStar}>*</span>
        </label>
        <select name="billing_type" value={formData.billing_type || ''} onChange={handleChange}
          style={validationErrors.billing_type ? errorInputStyle : { ...inputStyle, fontSize: '1rem', padding: '0.85rem' }}>
          <option value="">Select Change Type...</option>
          {BILLING_TYPE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
        {validationErrors.billing_type && <div style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem' }}>{validationErrors.billing_type}</div>}
        {isTM && <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#92400e', fontWeight: '500' }}>T&M mode: Labour by trade (ST/OT/DT), Material/Subcontractors/Equipment with markup. Room & Board available below.</div>}
        {isMaterialSupply && <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#065f46', fontWeight: '500' }}>Material Supply Only: 0 labour hours is acceptable for this change type.</div>}
        {isTrackingOnly && <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#7e22ce', fontWeight: '500' }}>Tracking Only: This entry is for tracking purposes and does not require cost details.</div>}
      </div>

      {/* Status strip (auto-derived from the boxes below) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1.25rem', padding: '0.6rem 1rem', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
        <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#475569' }}>Status:</span>
        <span style={{
          padding: '3px 12px',
          borderRadius: '12px',
          fontSize: '0.85rem',
          fontWeight: '700',
          backgroundColor: currentStatusMeta.badgeBg,
          color: currentStatusMeta.badgeText,
          textDecoration: currentStatusMeta.strike ? 'line-through' : 'none',
          textDecorationColor: '#dc2626',
          border: currentStatusKey === 'pending' ? '1px solid #cbd5e1' : 'none',
        }}>
          {currentStatusMeta.label}
        </span>
        <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
          {isCancelledEntry ? 'Cancelled — restore via the Cancel/Restore toolbar button.'
            : isRejected ? 'Rejected manually. Click "Rejected" again to clear.'
            : 'Set automatically: Estimated $ → Pending, Submitted $ → Submitted, Approved $ → Approved. "Tracking Only" change type → Tracking.'}
        </span>
      </div>

      {/* Basic Info Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
        <div>
          <label style={labelStyle}>JOB#</label>
          <input type="text" value={jobNumber} disabled style={{ ...inputStyle, backgroundColor: '#f3f4f6' }} />
        </div>
        <div>
          <label style={labelStyle}>Discipline<span style={requiredStar}>*</span></label>
          <select name="discipline" value={formData.discipline || ''} onChange={handleChange}
            style={validationErrors.discipline ? errorInputStyle : inputStyle}>
            <option value="">Select Discipline...</option>
            {DISCIPLINES.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          {validationErrors.discipline && <div style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem' }}>{validationErrors.discipline}</div>}
        </div>
        <div>
          <label style={labelStyle}>Extra</label>
          <input type="text" name="new_extra_number" value={formData.new_extra_number || ''} onChange={handleChange}
            style={{ ...inputStyle, backgroundColor: '#f0fdf4' }} placeholder={loadingExtra ? 'Generating...' : 'Auto-generated'} />
        </div>
        <div>
          <label style={labelStyle}>Estimated Hours{!isTrackingOnly && !(isTM || isMaterialSupply) && <span style={requiredStar}>*</span>}</label>
          <input type="number" step="0.01" name="estimated_hours" value={formData.estimated_hours || ''} onChange={handleChange}
            style={validationErrors.estimated_hours ? errorInputStyle : inputStyle} placeholder="0" />
          <div style={{ fontSize: '0.7rem', color: (isTM || isMaterialSupply) ? '#065f46' : '#e65100', marginTop: '2px' }}>
            {(isTM || isMaterialSupply) ? '0 hours accepted for this type' : isTrackingOnly ? 'Optional for tracking' : 'Manual estimate (SWAG)'}
          </div>
          {validationErrors.estimated_hours && <div style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem' }}>{validationErrors.estimated_hours}</div>}
        </div>
        <div>
          <label style={labelStyle}>Estimated $ {!isTrackingOnly && <span style={requiredStar}>*</span>}</label>
          <input type="number" step="0.01" name="estimated_price" value={formData.estimated_price || ''} onChange={handleChange}
            style={validationErrors.estimated_price ? errorInputStyle : inputStyle} placeholder="0.00" />
          <div style={{ fontSize: '0.7rem', color: '#e65100', marginTop: '2px' }}>{isTrackingOnly ? 'Optional for tracking' : 'Manual estimate (SWAG)'}</div>
          {validationErrors.estimated_price && <div style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem' }}>{validationErrors.estimated_price}</div>}
        </div>
        <div>
          <label style={labelStyle}>Commencement Date</label>
          <input type="date" name="commencement_date" value={formData.commencement_date?.split('T')[0] || ''} onChange={handleChange} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Submitted Hours</label>
          <input type="number" value={formData.submitted_hours || 0} disabled
            style={{ ...inputStyle, backgroundColor: '#f3f4f6', fontWeight: '600' }} />
          <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '2px' }}>Auto-calculated from line items</div>
        </div>
        <div>
          <label style={labelStyle}>Submitted $ </label>
          <input type="number" value={formData.submitted_price || 0} disabled
            style={{ ...inputStyle, backgroundColor: '#f3f4f6', fontWeight: '600' }} />
          <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '2px' }}>Auto-calculated from CO Total</div>
        </div>
        <div>
          <label style={labelStyle}>POCR #</label>
          <input type="text" name="pocr_number" value={formData.pocr_number || ''} onChange={handleChange} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>POCN#</label>
          <input type="text" name="pocn_number" value={formData.pocn_number || ''} onChange={handleChange} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Approved Price ($)</label>
          <input type="number" step="0.01" name="approved_price" value={formData.approved_price || ''} onChange={handleChange} style={inputStyle} />
          <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '2px' }}>Enter an approved amount to set status to Approved.</div>
        </div>
        <div>
          <label style={{ ...labelStyle, visibility: 'hidden' }} aria-hidden="true">Rejected</label>
          <button
            type="button"
            onClick={toggleRejected}
            title={isRejected ? 'Click to clear the Rejected status' : 'Mark this change as Rejected'}
            style={{
              width: '100%',
              padding: '0.75rem',
              boxSizing: 'border-box',
              whiteSpace: 'nowrap',
              borderRadius: '6px',
              fontSize: '0.9rem',
              fontWeight: '700',
              cursor: 'pointer',
              border: isRejected ? '1px solid #b91c1c' : '1px solid #d1d5db',
              backgroundColor: isRejected ? STATUS_META.rejected.badgeBg : '#fff',
              color: isRejected ? '#fee2e2' : '#b91c1c',
            }}
          >
            {isRejected ? 'Rejected ✓' : 'Rejected'}
          </button>
          <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '2px' }}>
            {isRejected ? 'Marked rejected — click again to clear.' : 'Marks this change as Rejected.'}
          </div>
        </div>
      </div>

      {/* Opened By */}
      <div style={{ marginTop: '1rem' }}>
        <label style={labelStyle}>Opened By</label>
        <input type="text" name="opened_by" value={formData.opened_by || ''} onChange={handleChange}
          style={{ ...inputStyle, backgroundColor: '#f3f4f6' }} readOnly={!initialData} />
      </div>

      {/* Description */}
      <div style={{ marginTop: '1rem' }}>
        <label style={labelStyle}>Description<span style={requiredStar}>*</span></label>
        <textarea name="description" value={formData.description || ''} onChange={handleChange} rows={3}
          style={validationErrors.description ? { ...errorInputStyle, resize: 'vertical' } : { ...inputStyle, resize: 'vertical' }}
          placeholder="Enter description of the change..." />
        {validationErrors.description && <div style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem' }}>{validationErrors.description}</div>}
      </div>

      {/* Drawing List */}
      <DynamicListSection
        title="Drawing List"
        items={formData.drawing_list || []}
        fields={[
          { key: 'reference', label: 'Reference', flex: 3 },
          { key: 'revision', label: 'Revision', flex: 1 },
          { key: 'link', label: 'Link', flex: 2, placeholder: 'URL (optional)' },
        ]}
        onChange={(items) => setFormData(prev => ({ ...prev, drawing_list: items }))}
        addLabel="Add Drawing"
      />

      {/* RFI List */}
      <DynamicListSection
        title="RFI List"
        items={formData.rfi_list || []}
        fields={[
          { key: 'reference', label: 'Reference', flex: 3 },
          { key: 'revision', label: 'Revision', flex: 1 },
          { key: 'link', label: 'Link', flex: 2, placeholder: 'URL (optional)' },
        ]}
        onChange={(items) => setFormData(prev => ({ ...prev, rfi_list: items }))}
        addLabel="Add RFI"
      />

      {/* Construction Costs */}
      <div style={sectionHeaderStyle}>Construction Costs</div>

      {/* Internal cost-builder notice (non-T&M only). Reminds users that this
          buildup stays internal and is NOT included in the client-facing PDF. */}
      {!isTM && (
        <div style={{ marginBottom: '0.75rem', padding: '0.6rem 1rem', backgroundColor: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
          <span style={{ fontWeight: '700', color: '#1e40af', marginRight: '0.4rem' }}>Internal cost estimate</span>
          <span style={{ fontSize: '0.8rem', color: '#1e3a8a' }}>
            Build the change value from estimated costs &amp; markup below. This detail is internal only — the
            downloaded/issued client PDF shows the resulting selling totals, not these cost or markup details.
          </span>
        </div>
      )}

      {wasMigrated && (
        <div style={{ marginBottom: '0.75rem', padding: '0.6rem 1rem', backgroundColor: '#fef9c3', borderRadius: '8px', border: '1px solid #fde68a' }}>
          <span style={{ fontWeight: '700', color: '#854d0e', marginRight: '0.4rem' }}>Converted entry</span>
          <span style={{ fontSize: '0.8rem', color: '#713f12' }}>
            This change order was created with the old cost fields and has been converted to the cost-builder.
            Markup was set to 0% so the original totals are unchanged — adjust markup and lines as needed.
          </span>
        </div>
      )}

      {/* Markup Percentage */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <label style={{ ...labelStyle, marginBottom: 0, whiteSpace: 'nowrap' }}>Markup %:</label>
        <input type="number" step="0.25" name="markup_percentage" value={formData.markup_percentage ?? 8.75} onChange={handleChange}
          style={{ ...inputStyle, width: '80px', padding: '0.4rem', textAlign: 'right' }} />
        <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>(applied to Material, Subcontractors, Equipment)</span>
      </div>

      {/* Labour by Trade */}
      <TMLabourSection
        trades={formData.tm_labour_items || []}
        onChange={(items) => setFormData(prev => ({ ...prev, tm_labour_items: items }))}
        markupPct={markupPct}
        config={config}
      />

      {/* Material with markup */}
      <MarkupListSection
        title="Material"
        items={formData.material_items || []}
        onChange={(items) => setFormData(prev => ({ ...prev, material_items: items }))}
        markupPct={markupPct}
        addLabel="Add Material"
      />

      {/* Subcontractors with markup */}
      <MarkupListSection
        title="Subcontractors"
        items={formData.subcontractor_items || []}
        onChange={(items) => setFormData(prev => ({ ...prev, subcontractor_items: items }))}
        markupPct={markupPct}
        addLabel="Add Subcontractor"
      />

      {/* Equipment with markup */}
      <MarkupListSection
        title="Equipment"
        items={formData.equipment_items || []}
        onChange={(items) => setFormData(prev => ({ ...prev, equipment_items: items }))}
        markupPct={markupPct}
        addLabel="Add Equipment"
      />

      {/* Room & Board + Transport (auto-derived per trade from Configuration) */}
      <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', backgroundColor: '#fef9c3', borderRadius: '8px', border: '1px solid #fde68a' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
          <div>
            <label style={{ ...labelStyle, fontWeight: '600' }}>Room & Board ($)</label>
            {/* type=text: type=number rejects thousands separators from fmtCurrency and renders blank */}
            <input type="text" value={fmtCurrency(formData.room_and_board)} readOnly disabled
              style={{ ...inputStyle, backgroundColor: '#f3f4f6', fontWeight: '600' }} />
            <div style={{ fontSize: '0.7rem', color: '#92400e', marginTop: '2px' }}>summed per trade</div>
          </div>
          <div>
            <label style={{ ...labelStyle, fontWeight: '600' }}>Transport ($)</label>
            <input type="text" value={fmtCurrency(formData.transport)} readOnly disabled
              style={{ ...inputStyle, backgroundColor: '#f3f4f6', fontWeight: '600' }} />
            <div style={{ fontSize: '0.7rem', color: '#92400e', marginTop: '2px' }}>summed per trade</div>
          </div>
        </div>
        <div style={{ fontSize: '0.7rem', color: '#92400e', marginTop: '6px' }}>
          Auto-calculated PER TRADE from Configuration (each trade's per-diem &divide; its hours/week) &times; that trade's hours. No markup applied. Adjust per-trade rates &amp; per-diems in the Configuration button.
        </div>
      </div>

      {/* Self-Assessed Tax (non-T&M only) */}
      {!isTM && (
        <div style={{ marginTop: '0.75rem' }}>
          <label style={labelStyle}>Self-Assessed Tax ($)</label>
          <input type="number" step="0.01" name="self_assessed_tax" value={formData.self_assessed_tax || ''} onChange={handleChange}
            style={{ ...inputStyle, maxWidth: '250px' }} placeholder="0.00" />
          <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '2px' }}>Included in Change Order Total (no markup applied)</div>
        </div>
      )}

      {/* Change Order Total */}
      <div style={{ padding: '0.75rem 1rem', backgroundColor: '#f0fdf4', borderRadius: '6px', border: '1px solid #bbf7d0', fontWeight: '700', fontSize: '1.05rem', textAlign: 'right', marginTop: '1rem' }}>
        Change Order Total: ${fmtCurrency(formData.change_order_total)}
      </div>

      {/* Schedule Impact */}
      <div style={sectionHeaderStyle}>Schedule Impact</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
        <div>
          <label style={labelStyle}>Shop (days)</label>
          <input type="number" step="0.5" name="schedule_shop_days" value={formData.schedule_shop_days || ''} onChange={handleChange} style={inputStyle} placeholder="0" />
        </div>
        <div>
          <label style={labelStyle}>Field (days)</label>
          <input type="number" step="0.5" name="schedule_field_days" value={formData.schedule_field_days || ''} onChange={handleChange} style={inputStyle} placeholder="0" />
        </div>
        <div>
          <label style={labelStyle}>Total (days)</label>
          <input type="number" value={formData.schedule_total_days || 0} disabled style={{ ...inputStyle, backgroundColor: '#f3f4f6' }} />
        </div>
      </div>

      {/* Clarification */}
      <div style={{ marginTop: '1rem' }}>
        <label style={labelStyle}>Clarification</label>
        <textarea name="clarification" value={formData.clarification || ''} onChange={handleChange} rows={3}
          style={{ ...inputStyle, resize: 'vertical', fontSize: '0.85rem' }} />
      </div>

      {/* Notes + Invoice */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginTop: '1rem' }}>
        <div>
          <label style={labelStyle}>Invoice #</label>
          <input type="text" name="invoice_number" value={formData.invoice_number || ''} onChange={handleChange} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Invoice Date</label>
          <input type="date" name="invoice_date" value={formData.invoice_date?.split('T')[0] || ''} onChange={handleChange} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>CTC/WBS</label>
          <input type="text" name="ctc_wbs" value={formData.ctc_wbs || ''} onChange={handleChange} style={inputStyle} />
        </div>
      </div>
      <div style={{ marginTop: '0.75rem' }}>
        <label style={labelStyle}>Notes</label>
        <textarea name="notes" value={formData.notes || ''} onChange={handleChange} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
      </div>

      {/* Buttons */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="button" onClick={handlePrintAndSubmit} icon="📄">
          Print PDF & {initialData ? 'Update' : 'Add Entry'}
        </Button>
        <Button type="submit" variant="primary">
          {initialData ? 'Update' : 'Add Entry'}
        </Button>
      </div>
    </form>
  );
});

export default ControlChartDashboard;
